import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  X,
  Usb,
  Search,
  RefreshCw,
  Check,
  AlertCircle,
  AlertTriangle,
  Download,
  Power,
  Zap,
  Waves,
  Activity,
  Radio,
  Cpu,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Terminal,
  Settings2,
  ShieldAlert,
  Lightbulb,
  Clock,
  RadioTower,
  Trash2,
  FolderOpen,
  FileClock,
  Save,
  GitCompareArrows,
  Bluetooth,
} from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { t as translate } from "i18n";
import Modal from "components/common/Modal";
import { useSerialPort } from "hooks/useSerialPort";
import { useSerialCommand } from "hooks/useSerialCommand";
import { useDeviceLink, LINK } from "hooks/useDeviceLink";
import useLiveFeed from "hooks/useLiveFeed";
import {
  parseExportStream,
  sliceLatestExport,
  buildExportResult,
  groupBySession,
  summarizeExport,
  REC_VERSION_MIN,
  REC_VERSION_MAX,
  DEFAULT_IMPACT_THRESHOLD_G,
  buildRecordCSV,
  LOAD_BIN_EDGES_G,
} from "utils/wearableExport";
import {
  parseFactoryLine,
  parseFactoryLines,
  LED_COLOR_HEX,
} from "utils/factoryTest";
import { showToast } from "utils/toast";
import SessionTimelineStrip from "./common/SessionTimelineStrip";
import ImpactFeatures from "./common/ImpactFeatures";
import VideoReview from "./common/VideoReview";
import {
  recordKey,
  loadReview,
  saveReview,
  mergeReview,
  REVIEW_LABEL,
} from "utils/videoReview";
import { sessionWindow, coverage, fmtDuration } from "utils/sessionTimeline";
import { severityForG } from "./common/impactVisuals";
import {
  tzOffsetLabel,
  fmtLocalTime,
  fmtElapsed,
  timeTooltip,
} from "utils/timeDisplay";
import { isWearablePort, wearablePorts } from "utils/wearablePort";
import {
  statusToDevCfg,
  STATUS_MARKER,
  ledStateSlug,
  foldLiveEvent,
  fwAtLeast,
  batteryDisplay,
} from "utils/wearableStatus";
import { saveTextFile, openTextFile } from "utils/fileDialog";
import {
  loadReviewSource,
  saveReviewSource,
  withReview,
  UNIT_COLORS,
} from "utils/reviewSources";
import {
  buildExportFile,
  parseExportFile,
  exportFileName,
  buildClockRecord,
} from "utils/exportFile";
import { wearableShortName } from "utils/centrifugeFile";
import betaAPI from "v2/api";
import SerialTerminal from "./common/SerialTerminal";
import ConfirmDialog from "./common/ConfirmDialog";
import CompareExportsView from "./CompareExportsView";
import BluetoothDfuFlow from "./BluetoothDfuFlow";
import { isBleDfuSupported } from "utils/bleDfu";
import FactoryTestFlow from "./FactoryTestFlow";
import CentrifugeFlow from "./CentrifugeFlow";
import UpdateFirmwareFlow from "./UpdateFirmwareFlow";
import CalibrationFlow from "./CalibrationFlow";
import DeviceWorkbench from "./DeviceWorkbench";
import { useDeviceRecord } from "hooks/useDeviceRecord";

const STEPS = {
  SCAN: "scan",
  CONNECTED: "connected",
};

// The v0.5.15+ USB export protocol. `export` streams a framed response whose
// =D payloads are hex record bytes; the decoder validates and groups them.
const EXPORT_CMD = {
  command: "export",
  startMarker: "=EXPORT",
  endMarker: "=DONE",
  includeStartMarker: true, // banner carries n=/fw=/mac=
  timeout: 20000, // idle gap between lines, not a total cap
  // The device streams unsolicited logs constantly, which re-arm the idle
  // timeout; without this, an unanswered `export` would hang forever. Give it
  // room for a connect-time reboot (see READ_CONFIG_CMD).
  maxWaitForStart: 12000,
  rejectOn: [
    {
      pattern: /\[export\]\s*refused/i,
      code: "SESSION_RUNNING",
      // Getter so the (user-facing) message is translated when read, not at
      // module load.
      get message() {
        return translate("wearableOps.errors.exportRefused");
      },
    },
  ],
};

// ── Device console commands (v0.5.15 firmware menu) ────────────────
// `help` reprints the standby menu, which carries every current config
// value — it doubles as the "read config" call. The setters each match
// the firmware's distinctive first response line; endMarker:null means
// "that one line is the whole response" (single-line finish in the
// useSerialCommand hook). After any set we re-read the menu so the UI
// always shows the device's own (clamped/applied) values.
const READ_CONFIG_CMD = {
  command: "help",
  startMarker: "S P O R T S", // unique to the menu banner
  endMarker: "type a command + Enter",
  timeout: 12000,
  // Connecting resets the wearable (DTR edge on open); it then spends ~2-3s
  // booting + in its pre-app DFU window before the console answers, so the
  // reply to a help sent right after connect can land several seconds later.
  maxWaitForStart: 12000,
};

// v0.5.38 machine protocol. `status` returns one `{"t":"status",…}` object with
// the full device config — a single-line JSON reply (endMarker:null = the marker
// line is the whole response). Replaces scraping the ASCII banner. (`ui on` is
// sent raw in the connect handshake, so it needs no command spec here.)
const STATUS_CMD = {
  command: "status",
  startMarker: STATUS_MARKER,
  endMarker: null,
  timeout: 6000,
  maxWaitForStart: 5000,
};

// v0.5.63 wall clock. `time` reports; `time <epoch>` sets. Single-line JSON reply
// `{"t":"time","result":…,"set":…,"utc":…,"epoch_s":…}`.
const TIME_CMD = {
  command: "time",
  startMarker: '"t":"time"',
  endMarker: null,
  timeout: 6000,
  maxWaitForStart: 5000,
};

// First firmware with the `erasestore` USB command (v0.5.85). Older firmware
// treats it as unknown, so the button is hidden entirely below this.
const ERASE_FW_MIN = [0, 5, 85];

// v0.5.93 fixed the firmware-side high-g scale: header peak fields written by
// OLDER firmware read ~25% low. Waveform samples are unaffected (raw counts,
// converted correctly UI-side) — only the header peaks carry the error.
const PEAK_FIX_FW = [0, 5, 93];

const CFG_CMDS = {
  impact: (g) => ({
    command: `threshold ${g}`,
    startMarker: "[det] impact threshold",
    endMarker: null,
    timeout: 6000,
    maxWaitForStart: 5000,
  }),
  event: (g) => ({
    command: `lowthresh ${g}`,
    startMarker: "[det] impact threshold", // fw prints both bands, impact line first
    endMarker: null,
    timeout: 6000,
    maxWaitForStart: 5000,
  }),
  broadcast: () => ({
    command: "broadcast",
    startMarker: "[bcast] mode ->",
    endMarker: null,
    timeout: 6000,
    maxWaitForStart: 5000,
  }),
  profile: () => ({
    command: "profile",
    startMarker: "[sensor] profile ->",
    endMarker: null,
    timeout: 8000, // touches the IMU over SPI
    maxWaitForStart: 6000,
  }),
  phy: () => ({
    command: "phy",
    startMarker: "[phy] session ->",
    endMarker: null,
    timeout: 6000,
    maxWaitForStart: 5000,
  }),
  castTx: () => ({
    // TOGGLE, not set-value (same convention as phy/broadcast). The prose
    // reply is not parsed for state — runCfgCmd's refresh re-reads `status`
    // and adopts cast_tx from there, per the firmware contract.
    command: "tx",
    startMarker: "[cast] tx ->",
    endMarker: null,
    timeout: 6000,
    maxWaitForStart: 5000,
  }),
  battery: () => ({
    command: "battery",
    startMarker: "Batt:",
    endMarker: null,
    timeout: 6000,
    maxWaitForStart: 5000,
  }),
  sync: () => ({
    command: "sync",
    startMarker: "[sync] connectable",
    endMarker: null,
    timeout: 6000,
    maxWaitForStart: 5000,
  }),
  dfu: () => ({
    command: "dfu",
    startMarker: "DFU advertising",
    endMarker: null,
    timeout: 8000,
    maxWaitForStart: 6000,
  }),
};

// Firmware clamps (mirrors sit_impact_detect.h — display hints only; the
// device re-clamps on its side and the UI re-reads the applied value).
const IMPACT_G_MIN = 4;
const IMPACT_G_MAX = 200;
// Event threshold allows 0 (= off); its non-zero floor is enforced device-side.
const EVENT_G_MAX = 16;

// Pull the current config out of the menu text.
const parseMenuConfig = (lines) => {
  const cfg = {};
  for (const raw of lines) {
    const l = String(raw);
    let m;
    if ((m = l.match(/Firmware\s+(v[\d.]+)/))) cfg.fw = m[1];
    if ((m = l.match(/State:\s*(\S+)/))) cfg.state = m[1];
    // Device MAC, if the firmware banner prints one (e.g. "MAC: D9:8E:C5:AA:19:84").
    // Normalise to bare uppercase hex for lookups.
    if (
      (m = l.match(
        /\bMAC:\s*([0-9A-Fa-f](?:[0-9A-Fa-f:.\- ]{10,20})[0-9A-Fa-f])/
      ))
    )
      cfg.mac = m[1].replace(/[^0-9a-fA-F]/g, "").toUpperCase();
    if ((m = l.match(/Sensor:\s*([A-Z]+)/))) cfg.profile = m[1];
    if ((m = l.match(/PHY:\s*(\S+)/))) cfg.phy = m[1];
    if ((m = l.match(/impact threshold \(now:\s*(\d+)\s*g\)/)))
      cfg.impactG = Number(m[1]);
    if ((m = l.match(/event threshold.*now:\s*(\d+)\s*g\)/)))
      cfg.eventG = Number(m[1]);
    if ((m = l.match(/ALL <-> MAX \(now:\s*(ALL|MAX)\)/))) cfg.broadcast = m[1];
  }
  return cfg;
};

// ── Severity helpers ────────────────────────────────────────────────

// Severity comes from the shared 10/25/50 g bands (impactVisuals) so the
// table, the timeline ticks, the meters and the live feed all agree. This
// panel used to carry its own 40/80 scale, which put a green 19.8 g row next
// to an amber 19.8 g tick in the same view.
const getSeverity = (g) => severityForG(g, false);

// `labelKey` is translated where rendered (never at module load).
const BAND = {
  impact: {
    labelKey: "common.entities.impact_one",
    color: "#DC2626",
    bg: "#FEF2F2",
    Icon: Zap,
  },
  event: {
    labelKey: "wearableOps.band.event",
    color: "#0EA5E9",
    bg: "#EFF6FF",
    Icon: Waves,
  },
};

// ── Formatting ──────────────────────────────────────────────────────

const fmtTime = (ms, isEpoch) => {
  if (ms == null) return "—";
  if (!isEpoch)
    return translate("wearableOps.format.uptimeMs", {
      value: ms.toLocaleString(),
    });
  const d = new Date(ms);
  if (d.getFullYear() <= 1970) return `${ms} ms`;
  return d.toISOString().replace("T", " ").slice(0, 19) + " UTC";
};

const fmtSessionStart = (ms, isEpoch) => {
  if (ms == null) return "—";
  if (!isEpoch) return translate("wearableOps.format.noTimeSync");
  const d = new Date(ms);
  if (d.getFullYear() <= 1970) return "—";
  return d.toLocaleString();
};

/**
 * The session's one-line time summary: date, window, zone and length. Replaces
 * three separate renderings of the same instants (divider date, "rec a → b",
 * and the strip's own caption) with one.
 */
const sessionHeadline = (session) => {
  const win = sessionWindow(session);
  if (!win || !win.isEpoch) return translate("wearableOps.format.noClockSync");
  const d = new Date(win.t0);
  const day = d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
  return `${day} · ${fmtLocalTime(win.t0)} → ${fmtLocalTime(
    win.t1
  )} ${tzOffsetLabel(win.t0)} · ${fmtDuration(win.spanMs)}`;
};

/** "40 s" of stored samples, or null when there is nothing to report. */
const sessionRecorded = (session) => {
  const win = sessionWindow(session);
  if (!win) return null;
  const cov = coverage(session, win);
  return cov.recordedMs > 0 ? fmtDuration(cov.recordedMs) : null;
};

/**
 * Device-vs-host clock drift at capture, in words. Silence when the clocks
 * agreed within a second: a "0 ms" badge is noise, a 40-second skew is the
 * reason two units' timelines will not line up.
 */
const clockNote = (clock) => {
  if (!clock) return null;
  if (clock.device_synced === false)
    return translate("wearableOps.clock.notSynced");
  const off = clock.offset_ms;
  if (off == null) return null;
  if (Math.abs(off) < 1000) return translate("wearableOps.clock.inStep");
  const s = (off / 1000).toFixed(1);
  return translate("wearableOps.clock.offset", {
    offset: `${off > 0 ? "+" : ""}${s}`,
  });
};

// ── Assignment history (super-user only) ────────────────────────────

const AssignmentHistory = ({ assignments }) => {
  const { t } = useTranslation();
  if (!assignments) return null;

  if (assignments.loading) {
    return (
      <div className="mt-4 flex items-center gap-2 py-3">
        <RefreshCw size={12} className="animate-spin text-[#9CA3AF]" />
        <span className="text-[12px] text-[#9CA3AF]">
          {t("wearableOps.assignments.loading")}
        </span>
      </div>
    );
  }

  if (assignments.error) {
    return (
      <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#FEF2F2] px-3 py-2">
        <AlertCircle size={12} className="flex-shrink-0 text-[#DC2626]" />
        <p className="text-[11px] text-[#DC2626]">
          {t("wearableOps.assignments.loadError", {
            error: assignments.error,
          })}
        </p>
      </div>
    );
  }

  const results = assignments.data?.results || [];
  const totalCount = assignments.data?.total_count ?? results.length;

  if (results.length === 0) {
    return (
      <div className="mt-4 py-3 text-center">
        <p className="text-[12px] text-[#9CA3AF]">
          {t("wearableOps.assignments.empty")}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <p className="mb-2 text-[13px] font-medium text-[#111827]">
        {t("wearableOps.assignments.title", { count: totalCount })}
      </p>
      <div className="max-h-[220px] overflow-y-auto rounded-lg border border-[#F3F4F6]">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#F3F4F6] bg-[#F9FAFB]">
              {["session", "team", "athlete", "date"].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-[#6B7280]"
                >
                  {t(`wearableOps.assignments.columns.${h}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results.map((a, i) => (
              <tr
                key={i}
                className="border-b border-[#F3F4F6] last:border-b-0 hover:bg-[#FAFAFA]"
              >
                <td className="px-3 py-2 text-[12px] text-[#374151]">
                  {a.session_name || "—"}
                </td>
                <td className="px-3 py-2 text-[12px] text-[#374151]">
                  {a.team_name || "—"}
                </td>
                <td className="px-3 py-2 text-[12px] text-[#374151]">
                  {a.player_name || "—"}
                </td>
                <td className="px-3 py-2 text-[12px] text-[#6B7280]">
                  {a.session_date
                    ? new Date(a.session_date).toLocaleDateString()
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Waveform chart ──────────────────────────────────────────────────

const AXES = [
  { key: "x", label: "X", color: "#6366F1" },
  { key: "y", label: "Y", color: "#10B981" },
  { key: "z", label: "Z", color: "#E07A3A" },
  { key: "mag", label: "|a|", color: "#111827" },
];

const WaveformTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="min-w-[150px] rounded-lg border border-[#F3F4F6] bg-white px-3 py-2.5 shadow-lg">
      <p className="mb-1.5 text-[11px] font-medium text-[#6B7280]">
        t = {d.t.toFixed(1)} ms
      </p>
      <div className="space-y-1 border-t border-[#F3F4F6] pt-1.5">
        {AXES.map(({ key, label, color }) => (
          <div key={key} className="flex items-center justify-between gap-3">
            <span
              className="flex items-center gap-1 text-[11px]"
              style={{ color }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: color }}
              />
              {label}
            </span>
            <span className="text-xs font-semibold text-[#111827]">
              {d[key] != null ? `${d[key].toFixed(2)}g` : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const WaveformChart = ({ samples, peakTimeMs, visibleAxes }) => {
  const { t } = useTranslation();
  if (!samples?.length) {
    return (
      <div className="flex h-[240px] items-center justify-center text-sm text-[#9CA3AF]">
        {t("wearableOps.waveform.empty")}
      </div>
    );
  }
  return (
    <div className="h-[240px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={samples}
          margin={{ top: 8, right: 12, left: -12, bottom: 18 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#F3F4F6"
          />
          <XAxis
            dataKey="t"
            type="number"
            domain={["dataMin", "dataMax"]}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: "#9CA3AF" }}
            tickFormatter={(v) => `${Math.round(v)}`}
            dy={6}
            label={{
              value: t("wearableOps.waveform.xAxisLabel"),
              position: "insideBottom",
              offset: -8,
              style: { fontSize: 10, fill: "#9CA3AF" },
            }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: "#9CA3AF" }}
            dx={-4}
            label={{
              value: "g",
              angle: -90,
              position: "insideLeft",
              offset: 22,
              style: { fontSize: 10, fill: "#9CA3AF" },
            }}
          />
          <Tooltip content={<WaveformTooltip />} />
          {peakTimeMs != null && (
            <ReferenceLine
              x={peakTimeMs}
              stroke="#DC2626"
              strokeDasharray="4 3"
              strokeWidth={1}
              label={{
                value: t("wearableOps.waveform.peakMarker"),
                position: "top",
                fill: "#DC2626",
                fontSize: 10,
              }}
            />
          )}
          {AXES.filter((a) => visibleAxes[a.key]).map(({ key, color }) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={color}
              strokeWidth={key === "mag" ? 1.75 : 1.25}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── Record detail (metadata + waveform) ─────────────────────────────

// ── Collapsible sidebar section ─────────────────────────────────────
// Keeps the left panel quiet: occasional-use tools (config, console, system)
// fold away behind a disclosure, with a summary shown while collapsed.

const CollapsibleSection = ({
  icon: Icon,
  title,
  summary,
  open,
  onToggle,
  right,
  children,
}) => (
  <div className="mt-3 overflow-hidden rounded-lg border border-[#F3F4F6]">
    <div
      className={`flex w-full items-center gap-2 px-3 py-2 ${
        open ? "border-b border-[#F3F4F6] bg-[#FAFAFA]" : ""
      }`}
    >
      <button
        onClick={onToggle}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        {open ? (
          <ChevronDown size={13} className="flex-shrink-0 text-[#9CA3AF]" />
        ) : (
          <ChevronRight size={13} className="flex-shrink-0 text-[#9CA3AF]" />
        )}
        {Icon && <Icon size={13} className="flex-shrink-0 text-[#6B7280]" />}
        <span className="text-[12px] font-medium text-[#374151]">{title}</span>
        {!open && summary && (
          <span className="min-w-0 truncate text-[11px] text-[#9CA3AF]">
            {summary}
          </span>
        )}
      </button>
      {right}
    </div>
    {open && <div className="p-2.5">{children}</div>}
  </div>
);

// A quiet key/value pair. Deliberately NOT a card — a grid of identical
// bordered tiles is what made the old detail panel unreadable.
const Spec = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <dt className="text-[11px] text-[#9CA3AF]">{label}</dt>
    <dd className="text-right text-[12px] font-medium text-[#374151]">
      {value}
    </dd>
  </div>
);

const RecordDetail = ({ record, onBack, index, total, onPrev, onNext }) => {
  const { t } = useTranslation();
  // Events read best from the low-g block (fine, denoised, 240 Hz); impacts
  // from the high-g block (max-mag envelope preserves the spike).
  const defaultBlock = record.band === "impact" ? "high" : "low";
  const [block, setBlock] = useState(defaultBlock);
  const [showSpecs, setShowSpecs] = useState(false);
  const atFirst = index <= 0;
  const atLast = index >= total - 1;

  // Arrow keys step records. Up/Left = previous, Down/Right = next — the
  // vertical pair matches the list's reading order, the horizontal pair is a
  // reflex for most people. Escape is deliberately NOT bound: the Modal
  // already owns it for close, and hijacking it would fire both.
  useEffect(() => {
    const onKey = (e) => {
      // Never steal keys from a field — the console input lives one panel over.
      const el = e.target;
      if (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.tagName === "SELECT" ||
          el.isContentEditable)
      ) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        onPrev?.();
      } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        onNext?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onPrev, onNext]);
  // Per-axis visibility, toggled by clicking the legend. |a| defaults off.
  const [visibleAxes, setVisibleAxes] = useState({
    x: true,
    y: true,
    z: true,
    mag: false,
  });

  // Reset block when a different record is selected.
  const recKey = `${record.sessionId}-${record.impactId}-${record.frameIndex}`;
  const lastKey = useRef(recKey);
  if (lastKey.current !== recKey) {
    lastKey.current = recKey;
    setBlock(defaultBlock);
  }

  const samples = block === "high" ? record.highG : record.lowG;
  const bandInfo = BAND[record.band];
  const sev = getSeverity(record.peakMagG);

  return (
    <div>
      {/* Breadcrumb + record stepper */}
      <div className="mb-4 flex items-center justify-between gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-[12px] font-medium text-[#6B7280] transition duration-150 hover:text-[#111827]"
        >
          <ChevronLeft size={14} />
          {t("wearableOps.recordDetail.allRecords")}
        </button>

        <div className="flex items-center gap-2">
          <span className="text-[11px] tabular-nums text-[#9CA3AF]">
            {t("wearableOps.recordDetail.position", {
              index: index + 1,
              total,
            })}
          </span>
          <div className="flex items-center overflow-hidden rounded-md border border-[#E5E7EB]">
            <button
              onClick={onPrev}
              disabled={atFirst}
              title={t("wearableOps.recordDetail.previousRecord")}
              className="flex h-6 w-6 items-center justify-center text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] hover:text-[#111827] disabled:pointer-events-none disabled:text-[#D1D5DB]"
            >
              <ChevronUp size={13} />
            </button>
            <span className="h-6 w-px bg-[#E5E7EB]" />
            <button
              onClick={onNext}
              disabled={atLast}
              title={t("wearableOps.recordDetail.nextRecord")}
              className="flex h-6 w-6 items-center justify-center text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] hover:text-[#111827] disabled:pointer-events-none disabled:text-[#D1D5DB]"
            >
              <ChevronDown size={13} />
            </button>
          </div>
        </div>
      </div>

      {/* Hero: the one number that matters, with the axis split beneath it */}
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <div className="mb-1.5 flex items-center gap-2">
            <bandInfo.Icon size={14} style={{ color: bandInfo.color }} />
            <span
              className="text-[13px] font-semibold"
              style={{ color: bandInfo.color }}
            >
              {t("wearableOps.recordDetail.bandTitle", {
                band: t(bandInfo.labelKey),
                id: record.impactId,
              })}
            </span>
            <span className="text-[11px] text-[#9CA3AF]">
              {t("wearableOps.recordDetail.session", { id: record.sessionId })}
            </span>
            {!record.isValid && (
              <span className="flex items-center gap-1 rounded bg-[#FEF2F2] px-1.5 py-0.5 text-[10px] font-medium text-[#DC2626]">
                <ShieldAlert size={10} />
                {t("wearableOps.recordDetail.failedValidation")}
              </span>
            )}
          </div>
          <p
            className="text-[34px] font-bold leading-none tracking-tight"
            style={{ color: sev.color }}
          >
            {record.peakMagG.toFixed(1)}
            <span className="ml-1 text-[16px] font-medium text-[#9CA3AF]">
              {t("wearableOps.recordDetail.gPeak")}
            </span>
          </p>
          <p className="mt-1.5 font-mono text-[11px] text-[#9CA3AF]">
            X {record.peakXG.toFixed(1)} · Y {record.peakYG.toFixed(1)} · Z{" "}
            {record.peakZG.toFixed(1)}
          </p>
        </div>
        {/* Timing — the only other numbers worth surfacing up front */}
        <dl className="flex gap-6 text-right">
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
              {t("wearableOps.recordDetail.rise")}
            </dt>
            <dd className="mt-0.5 text-[15px] font-semibold text-[#111827]">
              {record.riseMs} ms
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
              {t("wearableOps.recordDetail.duration")}
            </dt>
            <dd className="mt-0.5 text-[15px] font-semibold text-[#111827]">
              {record.durationMs} ms
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
              {t("wearableOps.recordDetail.time")}
            </dt>
            <dd className="mt-0.5 text-[15px] font-semibold text-[#111827]">
              {record.timestampIsEpoch
                ? new Date(record.timestampMs).toLocaleTimeString()
                : `+${(record.timestampMs / 1000).toFixed(1)}s`}
            </dd>
          </div>
        </dl>
      </div>

      {/* Block toggle */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1 rounded-lg bg-[#F3F4F6] p-0.5">
          {[
            {
              key: "low",
              label: t("wearableOps.recordDetail.lowGBlock", {
                count: record.lowG.length,
              }),
            },
            {
              key: "high",
              label: t("wearableOps.recordDetail.highGBlock", {
                count: record.highG.length,
              }),
            },
          ].map((b) => (
            <button
              key={b.key}
              onClick={() => setBlock(b.key)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition duration-150 ${
                block === b.key
                  ? "bg-white text-[#111827] shadow-sm"
                  : "text-[#6B7280] hover:text-[#374151]"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      <WaveformChart
        samples={samples}
        peakTimeMs={record.peakTimeMs}
        visibleAxes={visibleAxes}
      />

      {/* Interactive legend — click an axis to toggle it on the chart */}
      <div className="mt-2 flex items-center justify-center gap-2">
        {AXES.map((a) => {
          const on = visibleAxes[a.key];
          return (
            <button
              key={a.key}
              onClick={() =>
                setVisibleAxes((prev) => ({ ...prev, [a.key]: !prev[a.key] }))
              }
              title={t(
                on
                  ? "wearableOps.recordDetail.hideAxis"
                  : "wearableOps.recordDetail.showAxis",
                { axis: a.label }
              )}
              className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] transition duration-150 ${
                on
                  ? "text-[#374151] hover:bg-[#F3F4F6]"
                  : "text-[#C4C8CE] line-through hover:bg-[#F3F4F6]"
              }`}
            >
              <span
                className="h-2 w-2 rounded-full transition-opacity duration-150"
                style={{ backgroundColor: a.color, opacity: on ? 1 : 0.3 }}
              />
              {a.label}
            </button>
          );
        })}
      </div>

      {record.errors?.length > 0 && (
        <div className="mt-3 rounded-lg bg-[#FEF2F2] px-3 py-2">
          {record.errors.map((e, i) => (
            <p key={i} className="text-[11px] text-[#DC2626]">
              {e}
            </p>
          ))}
        </div>
      )}

      {/* Detection features (record v10+). Deliberately NOT behind the
          "Capture details" fold: capture config is reference material you read
          once, these are what the record is for. */}
      <ImpactFeatures record={record} />

      {/* Everything else lives here — capture config is reference material, not
          something you read on every record. */}
      <div className="mt-5 border-t border-[#F3F4F6] pt-3">
        <button
          onClick={() => setShowSpecs((v) => !v)}
          className="flex items-center gap-1.5 text-[11px] font-medium text-[#6B7280] transition duration-150 hover:text-[#111827]"
        >
          {showSpecs ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          {t("wearableOps.recordDetail.captureDetails")}
        </button>
        {showSpecs && (
          <dl className="mt-2 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
            <Spec
              label={t("wearableOps.recordDetail.specs.window")}
              value={`${record.sizeLabel} · ${record.windowSeconds ?? "?"} s`}
            />
            <Spec
              label={t("wearableOps.recordDetail.specs.sourceSamples")}
              value={record.sampleCount}
            />
            <Spec
              label={t("wearableOps.recordDetail.specs.lowGBand")}
              value={`${record.lowG.length} @ ${record.lowgHz ?? "?"} Hz · ±${
                record.lowgFsG
              } g`}
            />
            <Spec
              label={t("wearableOps.recordDetail.specs.highGBand")}
              value={`${record.highG.length} @ ${record.highgHz ?? "?"} Hz · ±${
                record.highgFsG
              } g`}
            />
            <Spec
              label={t("wearableOps.recordDetail.specs.imuProfile")}
              value={record.imuProfileLabel}
            />
            <Spec
              label={t("wearableOps.recordDetail.specs.overThreshold")}
              value={t("wearableOps.recordDetail.specs.samples", {
                count: record.overCount,
              })}
            />
            <Spec
              label={t("wearableOps.recordDetail.specs.sessionStart")}
              value={fmtSessionStart(
                record.sessionStartMs,
                record.sessionStartIsEpoch
              )}
            />
            <Spec
              label={t("wearableOps.recordDetail.specs.peakInstant")}
              value={fmtTime(record.timestampMs, record.timestampIsEpoch)}
            />
            <Spec
              label={t("wearableOps.recordDetail.specs.recordVersion")}
              value={record.recVersion}
            />
            <Spec
              label={t("wearableOps.recordDetail.specs.validation")}
              value={
                record.isValid
                  ? t("wearableOps.recordDetail.specs.allCrcsPassed")
                  : t("common.status.failed")
              }
            />
          </dl>
        )}
      </div>
    </div>
  );
};

// ── Record list ─────────────────────────────────────────────────────
// One flat table, session-grouped by subheader rows. A table (not a stack of
// cards) is the right affordance here: the whole point is comparing peak /
// rise / duration down a column.

// Verdict dot for the list. Deliberately a small mark, not a filled chip: a
// reviewed session would otherwise be a wall of colour and the peak column,
// which is what the list is for, would lose the fight.
// `textKey` is translated where rendered.
const VERDICT_DOT = {
  [REVIEW_LABEL.HEADER]: {
    color: "#059669",
    textKey: "wearableOps.verdict.header",
  },
  [REVIEW_LABEL.NOT_HEADER]: {
    color: "#DC2626",
    textKey: "wearableOps.verdict.notHeader",
  },
  [REVIEW_LABEL.FALSE_POSITIVE]: {
    color: "#9333EA",
    textKey: "wearableOps.verdict.falsePositive",
  },
  [REVIEW_LABEL.PRE_SESSION]: {
    color: "#6B7280",
    textKey: "wearableOps.verdict.preSession",
  },
  [REVIEW_LABEL.UNSURE]: {
    color: "#B45309",
    textKey: "wearableOps.verdict.unsure",
  },
};

// Same shortening as the review list, so a gate verdict reads identically
// wherever it appears.
// Keys are the firmware's gate labels (parsed, leave literal); values are
// translation keys for the short display form.
const GATE_SHORT_TABLE = {
  pass: "wearableOps.gate.pass",
  "too narrow": "wearableOps.gate.narrow",
  "too wide": "wearableOps.gate.wide",
  "no rotation": "wearableOps.gate.noRotation",
};
const gateShortLabel = (label) =>
  !label || label === "unknown" || label === "not evaluated"
    ? null
    : label
        .split(" + ")
        .map((p) => (GATE_SHORT_TABLE[p] ? translate(GATE_SHORT_TABLE[p]) : p))
        .join("+");

// Bin column headers from the scheme-1 edges: "3–4", "4–5", …, "10+".
const LOAD_BIN_LABELS = LOAD_BIN_EDGES_G.map((lo, i) =>
  i === LOAD_BIN_EDGES_G.length - 1
    ? `${lo}+`
    : `${lo}–${LOAD_BIN_EDGES_G[i + 1]}`
);

const fmtWindowStart = (w) => {
  if (w.windowStartMs == null) return "—";
  if (!w.windowStartIsEpoch) return `${w.windowStartMs.toLocaleString()} ms`;
  const d = new Date(w.windowStartMs);
  if (d.getFullYear() <= 1970) return `${w.windowStartMs} ms`;
  return d.toISOString().replace("T", " ").slice(11, 19) + " UTC";
};

// Brand orange, matching the dashboard SessionLoadChart's team-load area.
const LOAD_COLOR = "#E07A3A";

const LoadWindowsTooltip = ({ active, payload }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="min-w-[150px] rounded-lg border border-[#F3F4F6] bg-white px-3 py-2.5 shadow-lg">
      <p className="mb-1.5 text-[11px] font-medium text-[#6B7280]">
        {d.partial
          ? t("wearableOps.load.labelPartial", { label: d.label })
          : d.label}
      </p>
      <div className="space-y-1 border-t border-[#F3F4F6] pt-1.5">
        <div className="flex items-center justify-between gap-3">
          <span
            className="flex items-center gap-1 text-[11px]"
            style={{ color: LOAD_COLOR }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: LOAD_COLOR }}
            />
            {t("wearableOps.load.loadSumG")}
          </span>
          <span className="text-xs font-semibold text-[#111827]">
            {d.sumG.toFixed(1)} g
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] text-[#9CA3AF]">
            {t("wearableOps.load.peak")}
          </span>
          <span className="text-xs font-medium text-[#374151]">
            {d.peakG.toFixed(1)} g
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] text-[#9CA3AF]">
            {t("wearableOps.load.episodes")}
          </span>
          <span className="text-xs font-medium text-[#374151]">
            {d.episodes}
          </span>
        </div>
      </div>
    </div>
  );
};

// Load over time: Σg per 30 s window as an orange area, matching the
// dashboard's session load chart. Fed by the file/serial-decoded loadWindows
// (local only — this is NOT the DB load-graph, which is Pi-harvest data).
// X axis is elapsed since session start (startOffsetMs), so it reads the same
// whether the session clock was epoch-synced or on plain uptime.
const LoadWindowsChart = ({ windows }) => {
  const { t } = useTranslation();
  const data = React.useMemo(
    () =>
      (windows || []).map((w) => ({
        label: fmtElapsed(w.startOffsetMs) ?? "0:00:00",
        sumG: w.sumG,
        peakG: w.peakG,
        episodes: w.counts.reduce((a, c) => a + c, 0),
        partial: w.partial,
      })),
    [windows]
  );
  if (data.length === 0) return null;
  return (
    <div className="px-4 pt-2">
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-[#9CA3AF]">
        {t("wearableOps.load.chartTitle")}
      </div>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
          >
            <defs>
              <linearGradient id="loadWinFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={LOAD_COLOR} stopOpacity={0.35} />
                <stop offset="100%" stopColor={LOAD_COLOR} stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#F3F4F6"
            />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#9CA3AF" }}
              dy={8}
              minTickGap={40}
              interval="preserveStartEnd"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#9CA3AF" }}
              dx={-5}
              domain={[0, "auto"]}
            />
            <Tooltip
              content={<LoadWindowsTooltip />}
              cursor={{ stroke: "#E5E7EB", strokeWidth: 1 }}
            />
            <Area
              type="monotone"
              dataKey="sumG"
              name={t("wearableOps.load.loadSumG")}
              stroke={LOAD_COLOR}
              strokeWidth={2}
              fill="url(#loadWinFill)"
              isAnimationActive={false}
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Per-session low-g load histogram: one row per 30 s window. Not an impact
// (LOAD_BINS records, fw v0.6.16); rendered as its own compact table so a
// v0.6.16 export shows load rows alongside its impacts.
const LoadWindowsTable = ({ windows }) => {
  const { t } = useTranslation();
  if (!windows || windows.length === 0) return null;
  return (
    <div className="px-4 py-2">
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-[#9CA3AF]">
        {t("wearableOps.load.tableTitle", { count: windows.length })}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[#F3F4F6] text-[#9CA3AF]">
              <th className="py-1 pr-2 text-left text-[10px] font-medium">
                {t("wearableOps.load.start")}
              </th>
              {LOAD_BIN_LABELS.map((l) => (
                <th
                  key={l}
                  className="px-1.5 py-1 text-right text-[10px] font-medium tabular-nums"
                >
                  {l}
                </th>
              ))}
              <th className="px-1.5 py-1 text-right text-[10px] font-medium">
                Σ g
              </th>
              <th className="px-1.5 py-1 text-right text-[10px] font-medium">
                {t("wearableOps.load.peakG")}
              </th>
              <th className="px-1.5 py-1 text-center text-[10px] font-medium">
                {t("wearableOps.load.partial")}
              </th>
            </tr>
          </thead>
          <tbody>
            {windows.map((w, i) => (
              <tr key={i} className="border-b border-[#F9FAFB] last:border-b-0">
                <td className="py-1 pr-2 text-left text-[11px] tabular-nums text-[#6B7280]">
                  {fmtWindowStart(w)}
                </td>
                {w.counts.map((c, b) => (
                  <td
                    key={b}
                    className={`px-1.5 py-1 text-right text-[11px] tabular-nums ${
                      c > 0 ? "text-[#374151]" : "text-[#D1D5DB]"
                    }`}
                  >
                    {c}
                  </td>
                ))}
                <td className="px-1.5 py-1 text-right text-[11px] tabular-nums text-[#6B7280]">
                  {w.sumG.toFixed(1)}
                </td>
                <td className="px-1.5 py-1 text-right text-[11px] font-semibold tabular-nums text-[#374151]">
                  {w.peakG.toFixed(1)}
                </td>
                <td className="px-1.5 py-1 text-center text-[10px] text-[#9CA3AF]">
                  {w.partial ? "◐" : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RecordListRow = ({
  record,
  onSelect,
  onOpen,
  sessionStartMs,
  verdict,
  active,
}) => {
  const { t } = useTranslation();
  const bandInfo = BAND[record.band];
  const sev = getSeverity(record.peakMagG);
  // Elapsed since the session began — on a race day "T+3:49:28" places an
  // impact better than a wall clock does.
  const elapsed =
    record.timestampIsEpoch && sessionStartMs != null
      ? fmtElapsed(record.timestampMs - sessionStartMs, { compact: true })
      : null;
  return (
    <tr
      onClick={() => onSelect(record)}
      className={`group cursor-pointer border-b border-[#F3F4F6] transition duration-150 last:border-b-0 hover:bg-[#FAFAFA] ${
        active ? "bg-[#FFF7ED]" : ""
      }`}
    >
      <td className="py-2.5 pl-4 pr-2">
        <span className="flex items-center gap-2">
          <bandInfo.Icon
            size={13}
            style={{ color: bandInfo.color }}
            className="flex-shrink-0"
          />
          <span className="text-[12px] text-[#374151]">
            {t(bandInfo.labelKey)}
          </span>
        </span>
      </td>
      <td className="px-2 py-2.5 text-[12px] tabular-nums text-[#9CA3AF]">
        #{record.impactId}
      </td>
      {/* Peak carries severity via weight + color only — a filled chip on
          every row turned the list into confetti. */}
      <td
        className="px-2 py-2.5 text-right text-[13px] font-semibold tabular-nums"
        style={{ color: sev.color }}
      >
        {record.peakMagG.toFixed(1)} g
      </td>
      <td className="px-2 py-2.5 text-right text-[12px] tabular-nums text-[#6B7280]">
        {record.riseMs} ms
      </td>
      <td className="px-2 py-2.5 text-right text-[12px] tabular-nums text-[#6B7280]">
        {record.durationMs} ms
      </td>
      {/* What the firmware's dry-run gates would have decided. */}
      <td className="px-2 py-2.5 text-center">
        {gateShortLabel(record.gateFlagsLabel) && (
          <span
            title={t("wearableOps.gate.dryRunTitle", {
              label: record.gateFlagsLabel,
            })}
            className={`inline-block rounded px-1 py-px text-[10px] font-medium ${
              record.gateFlagsLabel === "pass"
                ? "bg-[#ECFDF5] text-[#047857]"
                : "bg-[#FFFBEB] text-[#B45309]"
            }`}
          >
            {gateShortLabel(record.gateFlagsLabel)}
          </span>
        )}
      </td>
      {/* And what you decided, once you have looked. */}
      <td className="px-2 py-2.5 text-center">
        {verdict && VERDICT_DOT[verdict] ? (
          <span
            className="text-[10px] font-medium"
            style={{ color: VERDICT_DOT[verdict].color }}
          >
            {t(VERDICT_DOT[verdict].textKey)}
          </span>
        ) : (
          <span className="text-[10px] text-[#D1D5DB]">
            {t("wearableOps.verdict.toCheck")}
          </span>
        )}
      </td>
      <td
        className="px-2 py-2.5 text-right text-[11px] tabular-nums text-[#9CA3AF]"
        title={timeTooltip(
          record.timestampMs,
          record.timestampIsEpoch,
          sessionStartMs
        )}
      >
        {record.timestampIsEpoch ? (
          <>
            {fmtLocalTime(record.timestampMs)}
            {elapsed && (
              <span className="ml-1.5 text-[#C9C4BD]">T+{elapsed}</span>
            )}
          </>
        ) : (
          `+${(record.timestampMs / 1000).toFixed(1)}s`
        )}
      </td>
      <td className="py-2.5 pl-2 pr-4 text-right">
        <span className="inline-flex items-center gap-1.5">
          {!record.isValid && (
            <ShieldAlert
              size={12}
              className="text-[#DC2626]"
              title={t("wearableOps.recordDetail.failedValidation")}
            />
          )}
          <ChevronRight
            size={14}
            onClick={
              onOpen
                ? (e) => {
                    e.stopPropagation();
                    onOpen(record);
                  }
                : undefined
            }
            title={
              onOpen ? t("wearableOps.recordList.openFullRecord") : undefined
            }
            className={`text-[#D1D5DB] transition duration-150 group-hover:text-[#E07A3A] ${
              onOpen ? "cursor-pointer hover:text-[#E07A3A]" : ""
            }`}
          />
        </span>
      </td>
    </tr>
  );
};

const RecordList = ({ sessions, onSelect, onOpen, labels, activeKey }) => {
  const { t } = useTranslation();
  // Label the column with the offset in force ON THE DAY, not today's: an
  // August session opened in January must still read UTC+1.
  const headerMs =
    sessions.find((s) => s.sessionStartIsEpoch)?.sessionStartMs ?? Date.now();
  return (
    <div className="overflow-hidden rounded-xl border border-[#F3F4F6] bg-white">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#F3F4F6]">
            {[
              {
                label: t("wearableOps.recordList.columns.type"),
                align: "left",
              },
              { label: t("wearableOps.recordList.columns.id"), align: "left" },
              {
                label: t("wearableOps.recordList.columns.peak"),
                align: "right",
              },
              {
                label: t("wearableOps.recordList.columns.rise"),
                align: "right",
              },
              {
                label: t("wearableOps.recordList.columns.duration"),
                align: "right",
              },
              {
                label: t("wearableOps.recordList.columns.gate"),
                align: "center",
              },
              {
                label: t("wearableOps.recordList.columns.verdict"),
                align: "center",
              },
              {
                label: t("wearableOps.recordList.columns.time", {
                  tz: tzOffsetLabel(headerMs),
                }),
                align: "right",
              },
              { label: "", align: "right" },
            ].map((c, i) => (
              <th
                key={i}
                className={`px-2 py-2 text-[10px] font-medium uppercase tracking-wide text-[#9CA3AF] ${
                  c.align === "right"
                    ? "text-right"
                    : c.align === "center"
                    ? "text-center"
                    : "text-left"
                } ${i === 0 ? "pl-4" : ""} ${i === 8 ? "pr-4" : ""}`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sessions.map((session) => (
            <React.Fragment key={session.sessionId}>
              {/* Session header + timeline as ONE band. The window used to
                  be printed twice — once here, once as a caption on the
                  strip — which is most of what made this look busy. */}
              <tr className="bg-[#FAFAFA]">
                <td colSpan={9} className="px-4 pb-1.5 pt-2">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-[11px] font-semibold text-[#374151]">
                      {t("wearableOps.recordDetail.session", {
                        id: session.sessionId,
                      })}
                    </span>
                    <span className="text-[10px] text-[#9CA3AF]">
                      {sessionHeadline(session)}
                    </span>
                    <span className="text-[10px] text-[#9CA3AF]">
                      ·{" "}
                      {[
                        session.impactCount &&
                          t("wearableOps.recordList.impactCount", {
                            count: session.impactCount,
                          }),
                        session.eventCount &&
                          t("wearableOps.recordList.eventCount", {
                            count: session.eventCount,
                          }),
                      ]
                        .filter(Boolean)
                        .join(", ") ||
                        t("wearableOps.recordList.nothingDetected")}
                    </span>
                    {(() => {
                      const rec = sessionRecorded(session);
                      return rec ? (
                        <span
                          className="text-[10px] text-[#B8B3AC]"
                          title={t("wearableOps.recordList.recordedTitle")}
                        >
                          {t("wearableOps.recordList.recorded", {
                            duration: rec,
                          })}
                        </span>
                      ) : null;
                    })()}
                    {session.intervals.length > 1 && (
                      <span className="text-[10px] text-[#B8B3AC]">
                        {t("wearableOps.recordList.runs", {
                          count: session.intervals.length,
                        })}
                      </span>
                    )}
                    {session.cutShort && (
                      <span
                        className="rounded bg-[#FEF3E7] px-1.5 py-0.5 text-[10px] font-medium text-[#E07A3A]"
                        title={t("wearableOps.recordList.cutShortTitle")}
                      >
                        {t("wearableOps.recordList.cutShort")}
                      </span>
                    )}
                  </div>
                  <SessionTimelineStrip session={session} onSelect={onSelect} />
                </td>
              </tr>
              {session.records.map((rec) => (
                <RecordListRow
                  onOpen={onOpen}
                  verdict={labels?.[recordKey(rec)]?.label}
                  active={activeKey != null && recordKey(rec) === activeKey}
                  key={`${rec.impactId}-${rec.frameIndex}`}
                  record={rec}
                  onSelect={onSelect}
                  sessionStartMs={
                    session.recordedIsEpoch
                      ? session.recordedStartMs
                      : session.sessionStartIsEpoch
                      ? session.sessionStartMs
                      : null
                  }
                />
              ))}
              {session.loadWindows && session.loadWindows.length > 0 && (
                <tr>
                  <td colSpan={9} className="bg-[#FCFCFD] p-0">
                    <LoadWindowsChart windows={session.loadWindows} />
                    <LoadWindowsTable windows={session.loadWindows} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── CSV export (record-level summary) ───────────────────────────────

const downloadExportCSV = (parsed, wearableId, review) => {
  const csv = buildRecordCSV(parsed.records, review);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const macFormatted = (parsed.mac || "unknown")
    .replace(/:/g, "-")
    .toUpperCase();
  a.href = url;
  a.download = `export-W${wearableId}-MAC${macFormatted}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ── Export results (top-level) ──────────────────────────────────────

const StatChip = ({ Icon, label, value, color, sub }) => (
  <div className="rounded-lg border border-[#F3F4F6] bg-white px-2.5 py-2">
    <div className="mb-0.5 flex items-center gap-1">
      <Icon size={10} style={color ? { color } : undefined} />
      <span className="text-[10px] text-[#9CA3AF]">{label}</span>
    </div>
    <span
      className="text-[15px] font-bold text-[#111827]"
      style={color ? { color } : undefined}
    >
      {value}
    </span>
    {sub && <span className="ml-1 text-[10px] text-[#9CA3AF]">{sub}</span>}
  </div>
);

const ExportResults = ({
  parsed,
  assignments,
  fieldLabel,
  onSaveFile,
  saveBusy,
  saveLabel: saveLabelProp,
  saveTitle: saveTitleProp,
  onCompare,
  imported,
  fileReview,
}) => {
  const { t } = useTranslation();
  const saveLabel = saveLabelProp ?? t("wearableOps.exportResults.saveFile");
  const saveTitle =
    saveTitleProp ?? t("wearableOps.exportResults.saveFileTitle");
  const summary = useMemo(() => summarizeExport(parsed), [parsed]);
  const sessions = useMemo(
    () => groupBySession(parsed.records),
    [parsed.records]
  );
  // null = list view. Drill down into one record, rather than showing a
  // cramped list and a full spec dump side by side.
  const [selected, setSelected] = useState(null);
  const [showWearablePrompt, setShowWearablePrompt] = useState(false);
  const [wearableIdInput, setWearableIdInput] = useState("");

  // ── Video review ──
  // The video is a local file and cannot survive a reload, but the verdicts
  // must: re-watching an hour of footage because a tab closed is exactly the
  // cost this is meant to remove. Keyed by device so two wearables reviewed on
  // one machine do not overwrite each other.
  // A saved export can carry its own review (file v3+). Opening one folds
  // those verdicts in, so a file reviewed on another machine arrives labelled
  // rather than blank -- with anything decided HERE winning, see mergeReview.
  const [review, setReview] = useState(() =>
    mergeReview(fileReview, loadReview(parsed.mac))
  );
  // Other units from the same session, reviewed against the same footage.
  // They live HERE rather than on a comparison screen because navigating away
  // unmounts the <video> and takes the loaded footage and the sync with it --
  // which made "add a second unit" cost the whole setup.
  const [extraUnits, setExtraUnits] = useState([]);
  const [addBusy, setAddBusy] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const addUnits = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setAddBusy(true);
    const next = [];
    for (const f of files) {
      try {
        next.push(
          await loadReviewSource(f, {
            colorIndex: 1 + next.length,
          })
        );
      } catch (err) {
        showToast.error(
          translate("wearableOps.exportResults.unitError", {
            name: f.name,
            message: err.message,
          })
        );
      }
    }
    if (next.length) {
      setExtraUnits((prev) => [
        ...prev,
        ...next.map((u, i) => ({
          ...u,
          color: UNIT_COLORS[(prev.length + i + 1) % UNIT_COLORS.length],
        })),
      ]);
    }
    setAddBusy(false);
  }, []);

  const saveUnit = useCallback(async (unit) => {
    setSavingId(unit.id);
    try {
      const res = await saveReviewSource(unit);
      if (!res?.canceled)
        showToast.success(
          translate("wearableOps.exportResults.unitSaved", { name: unit.name })
        );
    } catch (err) {
      showToast.error(
        translate("wearableOps.exportResults.unitError", {
          name: unit.name,
          message: err.message,
        })
      );
    } finally {
      setSavingId(null);
    }
  }, []);
  const [scrubTo, setScrubTo] = useState(null);
  const [activeRec, setActiveRec] = useState(null);
  useEffect(
    () => setReview(mergeReview(fileReview, loadReview(parsed.mac))),
    [parsed.mac, fileReview]
  );
  useEffect(() => {
    saveReview(parsed.mac, review);
  }, [parsed.mac, review]);
  const updateReview = useCallback(
    (fn) => setReview((r) => (typeof fn === "function" ? fn(r) : fn)),
    []
  );
  // Reviewing is on only while footage is actually loaded IN THIS TAB. The
  // saved review remembers a file name, but a blob URL cannot survive a reload
  // -- keying off the stored name would leave clicks scrubbing a player that
  // is not there, and never opening the record either.
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showAllRecords, setShowAllRecords] = useState(false);

  // Flat, session-ordered record order — the stepper walks the list exactly as
  // it reads on screen, crossing session boundaries.
  const flatRecords = useMemo(
    () => sessions.flatMap((s) => s.records),
    [sessions]
  );
  const step = useCallback(
    (delta) =>
      setSelected((cur) => {
        if (!cur) return cur;
        const i = flatRecords.indexOf(cur);
        if (i === -1) return cur;
        return flatRecords[i + delta] || cur; // clamp at both ends
      }),
    [flatRecords]
  );

  // A fresh export replaces the records; drop back to the list rather than
  // stranding the user on a detached record object.
  useEffect(() => {
    setSelected((cur) => (cur && flatRecords.indexOf(cur) === -1 ? null : cur));
    setActiveRec((cur) =>
      cur && flatRecords.indexOf(cur) === -1 ? null : cur
    );
  }, [flatRecords]);

  // With footage loaded, clicking an impact scrubs to it and arms it for a
  // verdict, staying on the list -- drilling into the record would hide the
  // player, which is the one thing you need to see. The row chevron still
  // opens the full record. Without footage, a click means what it always did.
  const handleSelect = useCallback(
    (rec) => {
      if (!videoLoaded) return setSelected(rec);
      setActiveRec(rec);
      // A fresh object each time, so clicking the same record twice scrubs
      // again rather than being swallowed as "no change".
      setScrubTo({ ms: rec.timestampMs });
      return undefined;
    },
    [videoLoaded]
  );

  // The file this screen was opened for, plus anything added since. One shape
  // for both, so the panel never has to care which came first.
  const reviewSources = useMemo(
    () => [
      {
        id: "primary",
        name: fieldLabel
          ? t("wearableOps.exportResults.unitName", { label: fieldLabel })
          : parsed.mac || t("wearableOps.exportResults.thisUnit"),
        color: UNIT_COLORS[0],
        records: flatRecords,
        review,
        offsetMs: review.offsetMs || 0,
        onReview: updateReview,
        onSave: onSaveFile,
        saving: saveBusy,
        fileName: imported?.fileName,
      },
      ...extraUnits.map((u) => ({
        ...u,
        onReview: (fn) =>
          setExtraUnits((prev) =>
            prev.map((x) => (x.id === u.id ? withReview(x, fn) : x))
          ),
        onOffset: (ms) =>
          setExtraUnits((prev) =>
            prev.map((x) =>
              x.id === u.id ? withReview(x, (r) => ({ ...r, offsetMs: ms })) : x
            )
          ),
        onSave: () => saveUnit(u),
        saving: savingId === u.id,
        onRemove: () =>
          setExtraUnits((prev) => prev.filter((x) => x.id !== u.id)),
      })),
    ],
    [
      flatRecords,
      review,
      updateReview,
      extraUnits,
      savingId,
      saveUnit,
      fieldLabel,
      parsed.mac,
      onSaveFile,
      saveBusy,
      imported?.fileName,
      t,
    ]
  );

  if (parsed.records.length === 0) {
    return (
      <div>
        <div className="py-6 text-center">
          <p className="text-sm text-[#6B7280]">
            {parsed.aborted.length > 0
              ? t("wearableOps.exportResults.noRecordsAborted", {
                  count: parsed.aborted.length,
                })
              : t("wearableOps.exportResults.noRecords")}
          </p>
          {parsed.mac && (
            <p className="mt-1 text-[11px] text-[#9CA3AF]">
              {parsed.fw
                ? t("wearableOps.exportResults.deviceWithFw", {
                    mac: parsed.mac,
                    fw: parsed.fw,
                  })
                : t("wearableOps.exportResults.device", { mac: parsed.mac })}
            </p>
          )}
        </div>
        <AssignmentHistory assignments={assignments} />
      </div>
    );
  }

  // Drill-down: a selected record owns the whole panel. Nothing else competes
  // with the waveform you came here to read.
  if (selected) {
    return (
      <RecordDetail
        record={selected}
        onBack={() => setSelected(null)}
        index={flatRecords.indexOf(selected)}
        total={flatRecords.length}
        onPrev={() => step(-1)}
        onNext={() => step(1)}
      />
    );
  }

  const hasIntegrityIssue = summary.invalid > 0 || summary.aborted > 0;

  return (
    <div>
      {imported && (
        <div className="mb-3 flex items-start gap-2.5 rounded-lg border border-[#DDEBFF] bg-[#F5F9FF] px-3 py-2">
          <FileClock
            size={16}
            className="mt-0.5 flex-shrink-0 text-[#2563EB]"
          />
          <div className="min-w-0 text-[12px] text-[#1E40AF]">
            <p className="font-medium">
              {t("wearableOps.exportResults.openedFromFile", {
                name: imported.wearableName,
              })}
            </p>
            <p className="text-[#3B82F6]">
              {imported.exportedAt
                ? t("wearableOps.exportResults.savedAt", {
                    date: new Date(imported.exportedAt).toLocaleString(),
                  })
                : t("wearableOps.exportResults.saveDateUnknown")}
              {imported.fileName ? ` · ${imported.fileName}` : ""}
            </p>
            {/* The device's own state when the dump was taken. Without the
                thresholds, "10 g" on a record means nothing months later. */}
            {imported.status ? (
              <p className="mt-1 font-mono text-[11px] text-[#3B82F6]">
                {[
                  imported.status.impact_g != null &&
                    t("wearableOps.exportResults.context.impact", {
                      g: imported.status.impact_g,
                    }),
                  imported.status.event_g != null &&
                    t("wearableOps.exportResults.context.event", {
                      g: imported.status.event_g,
                    }),
                  imported.status.phy &&
                    t("wearableOps.exportResults.context.phy", {
                      phy: imported.status.phy,
                    }),
                  imported.status.cast_tx === false
                    ? t("wearableOps.exportResults.context.silent")
                    : imported.status.cast_tx === true
                    ? t("wearableOps.exportResults.context.broadcasting")
                    : null,
                  imported.status.start_delay_s != null &&
                    t("wearableOps.exportResults.context.delay", {
                      seconds: imported.status.start_delay_s,
                    }),
                  imported.status.batt_pct != null &&
                    t("wearableOps.exportResults.context.battery", {
                      pct: imported.status.batt_pct,
                    }),
                  imported.status.cal_stored === true
                    ? t("wearableOps.exportResults.context.calibrated")
                    : imported.status.cal_stored === false
                    ? t("wearableOps.exportResults.context.uncalibrated")
                    : null,
                  clockNote(imported.clock),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-[#93B4E8]">
                {t("wearableOps.exportResults.noDeviceContext", {
                  version: imported.fileVersion ?? 1,
                })}
              </p>
            )}
          </div>
        </div>
      )}
      {/* Summary stats */}
      <div className="mb-3 grid grid-cols-3 gap-2 md:grid-cols-5">
        {/* Markers are session boundaries, not data — counting them here
            made the tiles look like they could not add up (14 + 6 ≠ 22). */}
        <StatChip
          Icon={Activity}
          label={t("wearableOps.exportResults.stats.records")}
          value={summary.total - (summary.markers || 0)}
          sub={
            summary.markers
              ? t("wearableOps.exportResults.stats.markers", {
                  count: summary.markers,
                })
              : undefined
          }
        />
        <StatChip
          Icon={Radio}
          label={t("common.entities.session_other")}
          value={summary.sessions}
        />
        <StatChip
          Icon={Zap}
          label={t("common.entities.impact_other")}
          value={summary.impacts}
          color="#DC2626"
        />
        <StatChip
          Icon={Waves}
          label={t("wearableOps.exportResults.stats.events")}
          value={summary.events}
          color="#0EA5E9"
        />
        <StatChip
          Icon={Activity}
          label={t("wearableOps.exportResults.stats.peakMag")}
          value={`${summary.peakMagG.toFixed(0)}g`}
          color={getSeverity(summary.peakMagG).color}
        />
      </div>

      {/* Device banner */}
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#6B7280]">
        {parsed.mac && (
          <span className="flex items-center gap-1">
            <Radio size={11} className="text-[#9CA3AF]" />
            {parsed.mac}
          </span>
        )}
        {parsed.fw && (
          <span className="flex items-center gap-1">
            <Cpu size={11} className="text-[#9CA3AF]" />
            {t("wearableOps.exportResults.fw", { fw: parsed.fw })}
          </span>
        )}
        {parsed.fw && !fwAtLeast(parsed.fw, PEAK_FIX_FW) && (
          <span
            className="text-[#B45309]"
            title={t("wearableOps.exportResults.peakScaleTitle")}
          >
            {t("wearableOps.exportResults.peakScaleWarning")}
          </span>
        )}
        {summary.expected != null && summary.expected !== summary.total && (
          <span className="text-[#F59E0B]">
            {t("wearableOps.exportResults.recordsReceived", {
              received: summary.total,
              expected: summary.expected,
            })}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          {onSaveFile && (
            <button
              onClick={onSaveFile}
              disabled={saveBusy}
              title={saveTitle}
              className="flex items-center gap-1 rounded-md bg-[#E07A3A] px-2.5 py-1 text-[11px] font-semibold text-white transition duration-150 hover:bg-[#D66B2E] disabled:opacity-50"
            >
              <Save size={11} />
              {saveBusy ? t("common.status.saving") : saveLabel}
            </button>
          )}
          {/* A different job from review: the signal board draws every unit's
              samples on one axis. Adding a unit TO the review happens in the
              review panel, without leaving the footage behind. */}
          {onCompare && (
            <button
              onClick={onCompare}
              title={t("wearableOps.exportResults.compareTitle")}
              className="flex items-center gap-1 rounded-md border border-[#E5E7EB] bg-white px-2.5 py-1 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
            >
              <GitCompareArrows size={11} />
              {t("wearableOps.exportResults.compareWaveforms")}
            </button>
          )}
          <button
            onClick={() => {
              // The printed field label IS the wearable id — when the unit
              // has one, download straight away instead of asking the
              // operator to retype it. The prompt remains the fallback for
              // unlabelled units.
              if (fieldLabel) {
                downloadExportCSV(parsed, String(fieldLabel));
                return;
              }
              setShowWearablePrompt((v) => !v);
            }}
            title={
              fieldLabel
                ? t("wearableOps.exportResults.csvTitleWithUnit", {
                    label: fieldLabel,
                  })
                : t("wearableOps.exportResults.csvTitle")
            }
            className="flex items-center gap-1 rounded-md border border-[#E5E7EB] bg-white px-2 py-1 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
          >
            <Download size={11} />
            {fieldLabel ? `CSV · ${fieldLabel}` : "CSV"}
          </button>
        </span>
      </div>

      {showWearablePrompt && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2">
          <span className="shrink-0 text-xs text-[#374151]">
            {t("wearableOps.exportResults.wearableIdLabel")}
          </span>
          <input
            autoFocus
            type="text"
            value={wearableIdInput}
            onChange={(e) => setWearableIdInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && wearableIdInput.trim()) {
                downloadExportCSV(parsed, wearableIdInput.trim(), review);
                setShowWearablePrompt(false);
                setWearableIdInput("");
              } else if (e.key === "Escape") {
                setShowWearablePrompt(false);
                setWearableIdInput("");
              }
            }}
            placeholder={t("wearableOps.exportResults.wearableIdPlaceholder")}
            className="w-20 rounded border border-[#E5E7EB] bg-white px-2 py-1 text-xs text-[#111827] outline-none focus:border-[#E07A3A]"
          />
          <button
            onClick={() => {
              if (!wearableIdInput.trim()) return;
              downloadExportCSV(parsed, wearableIdInput.trim());
              setShowWearablePrompt(false);
              setWearableIdInput("");
            }}
            className="rounded bg-[#E07A3A] px-2.5 py-1 text-xs font-medium text-white transition duration-150 hover:bg-[#D66B2E]"
          >
            {t("common.actions.download")}
          </button>
        </div>
      )}

      {/* Integrity warning */}
      {hasIntegrityIssue && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2">
          <AlertTriangle
            size={13}
            className="mt-0.5 flex-shrink-0 text-[#D97706]"
          />
          <div className="text-[11px] text-[#92400E]">
            <p>
              {summary.invalid > 0 &&
                t("wearableOps.exportResults.integrity.invalid", {
                  count: summary.invalid,
                })}
              {summary.aborted > 0 &&
                t("wearableOps.exportResults.integrity.aborted", {
                  count: summary.aborted,
                })}
              {t("wearableOps.exportResults.integrity.rerun")}
            </p>
            {/* Which check(s) failed — an all-records failure of one check is
                a decoder/firmware version mismatch, not corruption. */}
            {Object.keys(summary.failedChecks || {}).length > 0 && (
              <p className="mt-1 font-mono text-[10px]">
                {t("wearableOps.exportResults.integrity.failingChecks", {
                  checks: Object.entries(summary.failedChecks)
                    .map(([check, n]) => `${check} (${n})`)
                    .join(", "),
                })}
                {summary.recVersions?.length > 0 &&
                  t("wearableOps.exportResults.integrity.recordVersions", {
                    count: summary.recVersions.length,
                    versions: summary.recVersions.join(", "),
                    min: REC_VERSION_MIN,
                    max: REC_VERSION_MAX,
                  })}
              </p>
            )}
          </div>
        </div>
      )}

      {/* The workspace needs a real height to fill: this page scrolls, so
          `h-full` alone would collapse to the content. Sized against the
          viewport, floored so a short window still shows a usable list. */}
      <div
        className={videoLoaded ? "mb-3" : undefined}
        style={
          videoLoaded
            ? { height: "calc(100vh - 230px)", minHeight: 460 }
            : undefined
        }
      >
        <VideoReview
          sources={reviewSources}
          onAddUnits={addUnits}
          addBusy={addBusy}
          scrubTo={scrubTo?.ms ?? null}
          onScrubHandled={() => setScrubTo(null)}
          selectedRecord={activeRec}
          onSelectRecord={setActiveRec}
          onVideoLoaded={setVideoLoaded}
          // Reviewing takes the screen: the workspace is a video beside every
          // impact, and both want the height. Off before footage is loaded, so
          // the export summary still reads as a page.
          fill={videoLoaded}
        />
      </div>

      {/* While reviewing, the panel above owns the impact list. Showing the
          full table at the same time put the same seventeen rows on screen
          twice and made it unclear which one drove the video, so it folds
          away -- still one click from the columns the panel omits. */}
      {videoLoaded ? (
        <div className="border-t border-[#F3F4F6] pt-2">
          <button
            onClick={() => setShowAllRecords((v) => !v)}
            className="flex items-center gap-1.5 text-[11px] font-medium text-[#6B7280] transition duration-150 hover:text-[#111827]"
          >
            {showAllRecords ? (
              <ChevronDown size={13} />
            ) : (
              <ChevronRight size={13} />
            )}
            {t("wearableOps.exportResults.allRecords", {
              count: parsed.records.length,
            })}
          </button>
          {showAllRecords && (
            <RecordList
              sessions={sessions}
              onSelect={handleSelect}
              onOpen={setSelected}
              labels={review.labels}
              activeKey={activeRec ? recordKey(activeRec) : null}
            />
          )}
        </div>
      ) : (
        <RecordList
          sessions={sessions}
          onSelect={handleSelect}
          labels={review.labels}
          activeKey={activeRec ? recordKey(activeRec) : null}
        />
      )}

      <AssignmentHistory assignments={assignments} />
    </div>
  );
};

const ErrorBanner = ({ message, action }) => (
  <div className="mb-3 flex items-center justify-between gap-2 rounded-lg bg-[#FEF2F2] px-3 py-2.5">
    <div className="flex items-center gap-2">
      <AlertCircle size={14} className="flex-shrink-0 text-[#DC2626]" />
      <p className="text-xs text-[#DC2626]">{message}</p>
    </div>
    {action}
  </div>
);

// ── Main component ──────────────────────────────────────────────────

const WearableOpsModal = ({ show, onClose, autoConnectPath = null }) => {
  const { t } = useTranslation();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const isSuperUser = user?.is_superadmin === true;

  const {
    isAvailable,
    ports,
    connected,
    currentPort,
    receivedData,
    error,
    listPorts,
    connect,
    disconnect,
    send,
    sendRaw,
    kick,
    liveLineRef,
    exportRecords,
    onExportProgress,
    onWearableAttached,
    onPortsChanged,
  } = useSerialPort();

  const { executeCommand, isExecuting, cancel } = useSerialCommand();

  const [step, setStep] = useState(STEPS.SCAN);
  const [scanning, setScanning] = useState(false);
  const [selectedPort, setSelectedPort] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [exportResult, setExportResult] = useState(null); // { success, parsed } | { success:false, error, refused }
  const [assignments, setAssignments] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);
  const [endingSession, setEndingSession] = useState(false);
  const [passiveStreaming, setPassiveStreaming] = useState(false);
  // True only while the EXPORT command runs — the results panel keys off this,
  // not isExecuting, so config commands don't hijack it with "Streaming…".
  const [exportBusy, setExportBusy] = useState(false);
  // Records received so far during a binary export (from the main process), and
  // whether the device has begun streaming (vs. still deferring an idle-only
  // export while a session/offload finishes).
  const [exportProgress, setExportProgress] = useState(0);
  // "Erase all records" (v0.5.85): busy from the confirmed send until the
  // {"t":"sync","ev":"erase_done"} event (chunked erase, a few seconds).
  const [eraseBusy, setEraseBusy] = useState(false);
  const eraseBusyRef = useRef(false);
  useEffect(() => {
    eraseBusyRef.current = eraseBusy;
  }, [eraseBusy]);
  const [eraseDoneAt, setEraseDoneAt] = useState(null);
  // Full-fidelity export archive: the raw main-process result (record bytes
  // included) survives here so "Save file" can write it out; opened files
  // land in importedExport and render deviceless.
  const rawExportRef = useRef(null);
  // Device context captured at EXPORT time (see buildExportFile): the status
  // snapshot and clock comparison that make a dump interpretable later.
  const lastStatusRef = useRef(null);
  const exportContextRef = useRef(null);
  // applyStatusLines is declared further down; the export path runs long
  // after mount, so reach it through a ref rather than naming it in a deps
  // array above its declaration (temporal dead zone on every render).
  const applyStatusRef = useRef(null);
  const [exportSaveBusy, setExportSaveBusy] = useState(false);
  const [importedExport, setImportedExport] = useState(null);
  // A file carried into Compare from the single-file view, so the unit already
  // open does not have to be picked again.
  const [compareSeed, setCompareSeed] = useState(null);
  // Branded confirm dialog (replaces window.confirm): {title, body,
  // confirmLabel, danger, action}. One at a time is plenty here.
  const [confirmReq, setConfirmReq] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  // Bluetooth-only firmware update for units with no working USB console.
  const [bleDfuMode, setBleDfuMode] = useState(false);
  const bleDfuModeRef = useRef(false);
  bleDfuModeRef.current = bleDfuMode;
  const bleDfuBusyRef = useRef(false);
  const setBleDfuBusy = useCallback((busy) => {
    bleDfuBusyRef.current = busy;
  }, []);
  const [exportTotal, setExportTotal] = useState(null); // banner n, for a % bar
  const [exportStarted, setExportStarted] = useState(false);
  // Post-connect readiness: the device reboots on connect, so its console is
  // dead for a few seconds. We hold all commands until a probe confirms it is
  // alive — sending during boot leaves bytes buffered on the device, to be run
  // (confusingly) on the NEXT connect.
  // Link liveness is now derived from useDeviceLink, not stored as flags.
  // These two timestamps are the only inputs it needs from the raw stream:
  // when the console last proved it was alive, and when a pushed event last
  // arrived (which tells the poll whether the event stream can be trusted).
  const [aliveAt, setAliveAt] = useState(0);
  const [eventAt, setEventAt] = useState(0);
  // Raw diagnostic console (find the line ending the device actually accepts).
  const [consoleInput, setConsoleInput] = useState("help");
  const [consoleEnding, setConsoleEnding] = useState("\n");
  // Register / factory-test / centrifuge flows each take over the workbench
  // center column when active.
  const [factoryMode, setFactoryMode] = useState(false);
  const [centrifugeMode, setCentrifugeMode] = useState(false);
  const [calibrateMode, setCalibrateMode] = useState(false);
  const [updateMode, setUpdateMode] = useState(false); // guided DFU flow
  // Whether the connected device is already registered (from the dossier's MAC
  // lookup): null = unknown, true = registered, false = not registered. Drives
  // the primary button's label — a registered unit just re-runs the test.
  const [deviceRegistered, setDeviceRegistered] = useState(null);
  const [openSections, setOpenSections] = useState({
    settings: false,
    console: false,
    system: false,
    leds: false,
  });
  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  const [terminalHeight, setTerminalHeight] = useState(0);
  // Device configuration (parsed from the firmware's `help` menu).
  const [devCfg, setDevCfg] = useState(null);
  // LED legend from the `ui on` / `leds` handshake ([{state,color,pattern}]).
  const [ledLegend, setLedLegend] = useState([]);
  const ledBufRef = useRef([]);
  const [cfgBusy, setCfgBusy] = useState(null); // key of the in-flight control
  const [impactInput, setImpactInput] = useState("");
  const [eventInput, setEventInput] = useState("");
  const lastTerminalHeightRef = useRef(160);
  const fullScreen = true;
  const scanKeyRef = useRef(0);
  // Cursor into receivedData for the passive stream scanner (below).
  const scanPosRef = useRef(0);
  // Mirrors consoleReady for the async handshake loop + passive effect to share
  // without stale-closure races.
  const readyRef = useRef(false);
  // True once a `{"t":"status"}` reply has been seen — the handshake keeps
  // sending `ui on` until then (even if the boot banner already tripped
  // readiness), so we reliably enter machine mode and get structured config.
  const statusSeenRef = useRef(false);

  // Identify the connected device and load its manufacturing record. Shared by
  // the workbench top bar and center column; also reports registration status
  // back so the action row can label "Register unit" vs "Factory test".
  const devCfgRef = useRef(devCfg);
  devCfgRef.current = devCfg;
  const record = useDeviceRecord(devCfg?.mac, {
    connected,
    send,
    kick,
    receivedData,
    onRegistered: setDeviceRegistered,
  });
  const recordRef = useRef(record);
  recordRef.current = record;

  const handleTerminalResizeStart = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = terminalHeight;
    // Suppress text selection / cursor flicker for the duration of the drag.
    document.body.style.userSelect = "none";
    document.body.style.cursor = "row-resize";
    const onMouseMove = (ev) => {
      const delta = startY - ev.clientY;
      setTerminalHeight(Math.max(0, Math.min(600, startHeight + delta)));
    };
    const onMouseUp = () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  // Guards for auto-detection: auto-scan runs once per modal open; auto-connect
  // fires at most once per open unless we return to the scan screen.
  const didAutoScanRef = useRef(false);
  const autoConnectingRef = useRef(false);

  const doConnect = useCallback(
    async (path) => {
      if (!path) return null;
      setConnecting(true);
      const result = await connect(path);
      setConnecting(false);
      if (result) setStep(STEPS.CONNECTED);
      return result;
    },
    [connect]
  );

  // Auto-connect when the plugged-in device is unambiguous: an explicitly
  // requested port (from an attach event), or exactly one wearable on the bus.
  // Two wearables ⇒ don't guess; preselect the first and let the operator pick.
  const tryAutoConnect = useCallback(
    async (portList, requestedPath) => {
      // A wearable plugged in mid Bluetooth update shouldn't take the screen.
      if (connected || autoConnectingRef.current || bleDfuModeRef.current)
        return;
      const wp = wearablePorts(portList);
      let target = null;
      if (requestedPath && portList.some((p) => p.path === requestedPath)) {
        target = requestedPath;
      } else if (wp.length === 1) {
        target = wp[0].path;
      } else if (wp.length > 1) {
        setSelectedPort((cur) => cur || wp[0].path);
        return;
      }
      if (!target) return;
      autoConnectingRef.current = true;
      setSelectedPort(target);
      const result = await doConnect(target);
      // On failure (e.g. port busy), let the operator retry manually.
      if (!result) autoConnectingRef.current = false;
    },
    [connected, doConnect]
  );

  const handleScan = async () => {
    setScanning(true);
    setSelectedPort(null);
    scanKeyRef.current += 1;
    const list = await listPorts();
    setScanning(false);
    // Preselect the wearable so a manual Connect is one click.
    const wp = wearablePorts(list);
    if (wp[0]) setSelectedPort((cur) => cur || wp[0].path);
    return list;
  };

  const handleConnect = async () => {
    if (!selectedPort) return;
    await doConnect(selectedPort);
  };

  // Reset auto-detection guards each time the modal is (re)opened.
  useEffect(() => {
    if (!show) {
      didAutoScanRef.current = false;
      autoConnectingRef.current = false;
    }
  }, [show]);

  // Clear the auto-connect latch when we fall back to the scan screen, so a
  // replug can auto-connect again.
  useEffect(() => {
    if (!connected) autoConnectingRef.current = false;
  }, [connected]);

  // On open: scan once, then auto-connect to an unambiguous wearable (or the
  // port an attach event asked for). Handles both "opened from the menu with a
  // device already plugged in" and "opened by an attach event".
  useEffect(() => {
    if (!show || !isAvailable || connected) return;
    if (didAutoScanRef.current) return;
    didAutoScanRef.current = true;
    let cancelled = false;
    (async () => {
      const list = await handleScan();
      if (cancelled) return;
      tryAutoConnect(list, autoConnectPath);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [show, isAvailable, connected]);

  // While the app is running: a wearable plugged in on the scan screen
  // auto-connects; the port list also live-refreshes on any change.
  useEffect(() => {
    if (!isAvailable) return undefined;
    const offChanged = onPortsChanged(() => {});
    const offAttached = onWearableAttached((port) => {
      if (!show || connected || autoConnectingRef.current) return;
      if (port?.path) tryAutoConnect([port], port.path);
    });
    return () => {
      offChanged();
      offAttached();
    };
    // eslint-disable-next-line
  }, [isAvailable, show, connected, onPortsChanged, onWearableAttached]);

  const handleDisconnect = async () => {
    if (isExecuting) cancel();
    await disconnect();
    setStep(STEPS.SCAN);
    setSelectedPort(null);
    setExportResult(null);
    setAssignments(null);
    setStatusMsg(null);
    setFactoryMode(false);
    setCentrifugeMode(false);
    setCalibrateMode(false);
    setUpdateMode(false);
    setDeviceRegistered(null);
    setLedLegend([]);
    ledBufRef.current = [];
  };

  // Fetch assignment history for a device (super-user only).
  const fetchAssignments = useCallback(
    async (mac) => {
      if (!mac || !isSuperUser) return;
      setAssignments({ loading: true, data: null, error: null });
      try {
        const res = await betaAPI.getWearableAssignments({ macAddress: mac });
        setAssignments({ loading: false, data: res.data, error: null });
      } catch (err) {
        setAssignments({ loading: false, data: null, error: err.message });
      }
    },
    [isSuperUser]
  );

  // Live record count while a binary export streams (drives the button label).
  useEffect(() => {
    const off = onExportProgress((p) => {
      setExportProgress(p?.received || 0);
      setExportTotal(p?.total ?? null);
      if (p?.started) setExportStarted(true);
    });
    return off;
  }, [onExportProgress]);

  // Reset the passive scan cursor on each (re)connection — receivedData is
  // cleared by the serial hook when a port opens.
  useEffect(() => {
    if (connected) {
      scanPosRef.current = 0;
      setPassiveStreaming(false);
    }
  }, [connected, currentPort]);

  // Passive stream capture. The device can stream an export unsolicited (it
  // dumps on USB connect) and interleaves log lines at any time, so instead of
  // only capturing what we explicitly requested, we watch the raw console and
  // parse any complete =EXPORT…=DONE block as it lands. Scans only newly
  // arrived lines (O(n) amortised) so a full-store dump doesn't bog the UI.
  useEffect(() => {
    if (!connected) return;
    // Always-on: receivedData is fed by useSerialPort independently of any
    // executeCommand, and scanPosRef guarantees each line is inspected once —
    // so this is the single source of truth for export results, no matter what
    // (button, config read in flight, or an unsolicited dump) triggered them.
    let sawExportOpen = false;
    let sawDone = false;
    let sawMenu = false;
    let sawAlive = false; // status/ack/nack ⇒ console is alive (v0.5.38)
    let sawEraseDone = false;
    // Accumulated devCfg patch from every live event this pass — status
    // objects plus the v0.5.81 state/substate pushes (and, on older firmware,
    // the state field acks/nacks and session events carry). Folded in arrival
    // order so the newest information wins.
    let livePatch = null;
    let ledComplete = 0; // n, once the last legend entry (i === n-1) arrives
    for (let i = scanPosRef.current; i < receivedData.length; i++) {
      const l = String(receivedData[i].data).trim();
      // includes(), not startsWith(): the console echoes typed input with a
      // bare \r, so the echo can arrive CR-glued to the banner as one line
      // ("export\r=EXPORT v1 …"). Neither marker string can occur inside =D
      // hex payloads (O/N/E/X aren't hex digits), so includes() is safe.
      if (l.includes("=EXPORT")) {
        sawExportOpen = true;
        sawDone = false;
      } else if (l.includes("=DONE")) {
        sawDone = true;
      }
      // The device auto-prints its menu banner on boot; either line is a
      // reliable "console is alive" marker (pre-0.5.38 fallback).
      if (l.includes("S P O R T S") || l.includes("type a command + Enter")) {
        sawMenu = true;
      }
      // v0.5.38 machine protocol: any NDJSON control line (status/id/ack/nack)
      // means the console is alive and in machine mode. Match on substring, not
      // a successful parse, so a truncated/garbled status line (e.g. a firmware
      // TX-buffer bug that chops it) still de-wedges readiness and stops the
      // `ui on` retry loop rather than hanging on "Console not responding".
      if (l.includes('"t":"')) {
        if (
          l.includes('"t":"status"') ||
          l.includes('"t":"id"') ||
          l.includes('"t":"ack"') ||
          l.includes('"t":"nack"')
        ) {
          sawAlive = true;
        }
        if (l.includes('"t":"status"')) statusSeenRef.current = true;
        // Fold EVERY object on the line — the echo/CR gluing means a line can
        // carry more than one event, and the old first-object parse silently
        // dropped the rest.
        for (const ev of parseFactoryLines(l)) {
          if (
            ev.t === "sync" &&
            (ev.ev === "erase_done" ||
              (ev.ev === "erase" && (ev.sectors ?? 0) === 0))
          )
            sawEraseDone = true;
          // LED legend (12 objects after the status on `ui on` / `leds`).
          // Slot by index; complete when the last entry (i === n-1) lands.
          if (ev.t === "led" && ev.i != null) {
            ledBufRef.current[ev.i] = {
              state: ev.state,
              color: ev.color,
              pattern: ev.pattern,
            };
            if (ev.n != null && ev.i === ev.n - 1) ledComplete = ev.n;
          } else {
            const base = livePatch ?? {};
            const folded = foldLiveEvent(base, ev);
            if (folded !== base) livePatch = folded;
          }
        }
      }
    }
    scanPosRef.current = receivedData.length;
    if (sawEraseDone && eraseBusyRef.current) setEraseDoneAt(Date.now());
    if (ledComplete) {
      setLedLegend(ledBufRef.current.slice(0, ledComplete).filter(Boolean));
    }

    // Readiness is detected from the raw stream, NOT from a probe we send: the
    // device prints its menu (and framed data) unprompted after its connect
    // reboot, and this always-on scan sees it even though it landed before any
    // command listener could attach. This is why we never send `help` during
    // boot — doing so raced the banner and got swallowed.
    if (sawMenu || sawExportOpen || sawDone || sawAlive) {
      readyRef.current = true;
      // Every proof of life re-stamps this. useDeviceLink treats a fresh
      // stamp as "console answering", which is what makes recovery from a
      // missed handshake automatic rather than something the user must spot.
      setAliveAt(performance.now());
    }
    if (livePatch) setEventAt(performance.now());
    // Adopt device state/config from the folded live events (v0.5.38 status
    // objects; v0.5.81 state/substate pushes; ack/session events in between).
    // Only present fields are merged, so it never clobbers earlier values.
    if (livePatch) {
      const cfg = livePatch;
      setDevCfg((prev) => ({ ...prev, ...cfg }));
      // Threshold inputs track the device only when a full status carried
      // them — a bare state event must not reset a half-typed field.
      if (cfg.impactG != null) setImpactInput(String(cfg.impactG));
      if (cfg.eventG != null) setEventInput(String(cfg.eventG));
    } else if (sawMenu) {
      // Pre-0.5.38 fallback: scrape config straight from the boot banner.
      const cfg = parseMenuConfig(receivedData.map((d) => String(d.data)));
      if (Object.keys(cfg).length) {
        setDevCfg((prev) => ({ ...prev, ...cfg }));
        if (cfg.impactG != null) setImpactInput(String(cfg.impactG));
        if (cfg.eventG != null) setEventInput(String(cfg.eventG));
      }
    }

    if (sawDone) {
      setPassiveStreaming(false);
      const block = sliceLatestExport(receivedData.map((d) => String(d.data)));
      if (block) {
        const parsed = parseExportStream(block.lines, {
          impactThresholdG: DEFAULT_IMPACT_THRESHOLD_G,
        });
        // v3 (enc=bin) record bytes can't be reconstructed from the line-split
        // stream — the Export button's binary reader owns that. Only trust the
        // passive text capture for v1/v2.
        if (
          (parsed.version == null || parsed.version < 3) &&
          (parsed.records.length || parsed.recordCount != null)
        ) {
          setStatusMsg(null);
          setExportResult({ success: true, parsed });
          if (parsed.mac) fetchAssignments(parsed.mac);
        }
      }
    } else if (sawExportOpen) {
      setPassiveStreaming(true);
    }
  }, [receivedData, connected, fetchAssignments]);

  // One wake cycle: send THEN kick, in that order. Observed on hardware that
  // kick-then-send fails (an early kick after open doesn't take and the
  // follow-up just queues), while queue-then-kick works every time -- the DTR
  // edge makes the console drain its FIFO and execute what is already there.
  // The kick is safe: uptime is continuous across it, it does not reboot.
  // useDeviceLink owns the repetition and backoff; this owns the ordering.
  const wakeOnce = useCallback(async () => {
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    await sleep(300); // let buffered boot output land before we talk
    await send("ui on"); // queues if the console is deaf
    await sleep(1800);
    if (statusSeenRef.current) return;
    await kick(); // deaf -- force the drain
    await sleep(1200);
  }, [send, kick]);

  // Fire-and-forget: the reply is consumed by the passive scanner above, so
  // it must never go through executeCommand or it races the request/response
  // layer.
  const pollStatus = useCallback(() => {
    send("status");
  }, [send]);

  const link = useDeviceLink({
    connected,
    connecting,
    wake: wakeOnce,
    poll: pollStatus,
    lastEventAt: eventAt,
    lastStatusAt: aliveAt,
  });

  // Kept under the old names so the ~30 call sites below read unchanged; the
  // source of truth is now the machine.
  const consoleReady = link.isReady;
  const waking = link.phase === LINK.HANDSHAKING;

  // Run the export via the main-process binary reader (v3 records carry raw
  // bytes that can't survive line-splitting) and decode. Throws on refusal /
  // no data.
  const runExportOnce = useCallback(async () => {
    const main = await exportRecords();
    const parsed = buildExportResult(main || {}, {
      impactThresholdG: DEFAULT_IMPACT_THRESHOLD_G,
    });
    rawExportRef.current = main || null;
    exportContextRef.current = null; // stale context is worse than none
    if (parsed.refused) {
      const e = new Error(EXPORT_CMD.rejectOn[0].message);
      e.refused = true;
      throw e;
    }
    // No-response guard: the reader resolves on =DONE or an idle gap. If nothing
    // framed came back, say so plainly.
    if (parsed.records.length === 0 && parsed.recordCount == null) {
      throw new Error(translate("wearableOps.errors.noExportData"));
    }
    // Snapshot the device NOW, while it still holds the state that produced
    // this dump. Best-effort: a failed read costs context in the saved file,
    // never the export itself.
    try {
      const st = await executeCommand(STATUS_CMD);
      applyStatusRef.current?.(st.lines);
      const hostMs = Date.now();
      const tm = await executeCommand(TIME_CMD);
      let timeEv = null;
      for (const l of tm.lines || []) {
        const ev = parseFactoryLine(String(l));
        if (ev?.t === "time") {
          timeEv = ev;
          break;
        }
      }
      exportContextRef.current = {
        status: lastStatusRef.current,
        clock: buildClockRecord(timeEv, hostMs),
      };
    } catch {
      exportContextRef.current = null;
    }
    return parsed;
  }, [exportRecords, executeCommand]);

  const handleExport = useCallback(
    async ({ autoRetryOnRefused = true } = {}) => {
      setExportResult(null);
      setAssignments(null);
      setStatusMsg(null);
      // A refusal can surface two ways: rejectOn throws (err.rejected) before
      // the banner, or the parser sees "[export] refused" (err.refused).
      const isRefused = (e) =>
        !!(e && (e.refused || e.rejected || e.code === "SESSION_RUNNING"));

      // Belt and braces: the manual path sets the parsed result itself AND the
      // passive capture effect will set the same result from the raw stream —
      // whichever lands first wins; they carry identical data. Never clobber a
      // success with an error the other path may have already resolved.
      const succeed = (parsed) => {
        setStatusMsg(null);
        setExportResult({ success: true, parsed });
        if (parsed.mac) fetchAssignments(parsed.mac);
      };
      const failIfNoResult = (err) =>
        setExportResult((prev) =>
          prev?.success
            ? prev
            : { success: false, error: err.message, refused: isRefused(err) }
        );

      setExportBusy(true);
      setExportProgress(0);
      setExportTotal(null);
      setExportStarted(false);
      try {
        succeed(await runExportOnce());
      } catch (err) {
        if (isRefused(err) && autoRetryOnRefused) {
          // Auto-recover: end the session, then retry export once.
          setStatusMsg(translate("wearableOps.status.retryingExport"));
          try {
            await send("idle");
            await new Promise((r) => setTimeout(r, 1200));
            succeed(await runExportOnce());
            return;
          } catch (retryErr) {
            setStatusMsg(null);
            failIfNoResult(retryErr);
            return;
          }
        }
        failIfNoResult(err);
      } finally {
        setExportBusy(false);
      }
    },
    [runExportOnce, send, fetchAssignments]
  );

  const handleEndSession = useCallback(async () => {
    setEndingSession(true);
    setStatusMsg(null);
    try {
      await send("idle");
      setStatusMsg(translate("wearableOps.status.sessionEnded"));
    } catch (err) {
      setStatusMsg(
        translate("wearableOps.status.endSessionFailed", {
          message: err.message,
        })
      );
    } finally {
      setEndingSession(false);
    }
  }, [send]);

  // Save the whole export — raw record bytes included — to a persistent
  // file. This is the fleet-review workflow's primary artifact: session →
  // export → save, per unit; the files reopen weeks later (no device) with
  // the exact same decoded view.
  const handleSaveExportFile = useCallback(async () => {
    if (!rawExportRef.current || exportSaveBusy) return;
    setExportSaveBusy(true);
    try {
      const { json } = buildExportFile({
        wearable: record.wearable,
        mac: devCfg?.mac,
        raw: rawExportRef.current,
        exportedAt: new Date().toISOString(),
        status: exportContextRef.current?.status ?? null,
        clock: exportContextRef.current?.clock ?? null,
        parsed: exportResult?.parsed ?? null,
        // Read back from storage rather than plumbed down from the review
        // panel: the panel writes every change there synchronously, and the
        // save button lives above it in the tree.
        review: loadReview(exportResult?.parsed?.mac ?? devCfg?.mac),
      });
      const res = await saveTextFile({
        defaultName: exportFileName({
          wearable: record.wearable,
          mac: devCfg?.mac,
        }),
        contents: json,
      });
      if (!res?.canceled)
        showToast.success(translate("wearableOps.toasts.exportSaved"));
    } catch (err) {
      showToast.error(
        err?.message || translate("wearableOps.toasts.exportSaveFailed")
      );
    } finally {
      setExportSaveBusy(false);
    }
  }, [record.wearable, devCfg?.mac, exportSaveBusy, exportResult]);

  /** Write an OPENED file back out, now carrying whatever was tagged against
   *  it. Same shape as saving a live export -- raw bytes verbatim, digest and
   *  review rebuilt -- and it defaults to the name it came from, so the usual
   *  answer is to overwrite the file in place. */
  const handleSaveImportedFile = useCallback(async () => {
    if (!importedExport?.raw || exportSaveBusy) return;
    setExportSaveBusy(true);
    try {
      const { json } = buildExportFile({
        wearable: importedExport.wearable,
        mac: importedExport.parsed?.mac,
        raw: importedExport.raw,
        // The export happened when it happened; tagging it later must not
        // rewrite when the data came off the device.
        exportedAt: importedExport.exportedAt,
        status: importedExport.status,
        clock: importedExport.clock,
        parsed: importedExport.parsed,
        // Read from storage rather than plumbed down: the review panel writes
        // every change there synchronously, and this button sits above it.
        review: loadReview(importedExport.parsed?.mac),
      });
      const res = await saveTextFile({
        defaultName:
          importedExport.fileName ||
          exportFileName({
            wearable: importedExport.wearable,
            mac: importedExport.parsed?.mac,
          }),
        contents: json,
      });
      if (!res?.canceled)
        showToast.success(translate("wearableOps.toasts.savedWithTagging"));
    } catch (err) {
      showToast.error(
        err?.message || translate("wearableOps.toasts.exportSaveFailed")
      );
    } finally {
      setExportSaveBusy(false);
    }
  }, [importedExport, exportSaveBusy]);

  const handleOpenExportFile = useCallback(async () => {
    try {
      const res = await openTextFile({
        filters: [
          {
            name: translate("wearableOps.toasts.savedExportFilter"),
            extensions: ["json"],
          },
        ],
      });
      if (res?.canceled) return;
      const file = parseExportFile(res.contents);
      const parsed = buildExportResult(file.raw, {
        impactThresholdG: DEFAULT_IMPACT_THRESHOLD_G,
      });
      setImportedExport({
        parsed,
        wearable: file.wearable,
        exportedAt: file.exportedAt,
        fileName: res.name || res.filePath || null,
        // v2 context — null on older files, which the banner says out loud
        // rather than leaving the reader to assume a config.
        fileVersion: file.fileVersion,
        status: file.status,
        clock: file.clock,
        review: file.review,
        // Kept so the file can be written back with its tagging, and so
        // Compare can be seeded with it instead of making you pick it twice.
        raw: file.raw,
        contents: res.contents,
      });
    } catch (err) {
      showToast.error(
        err?.message || translate("wearableOps.toasts.openFileFailed")
      );
    }
  }, []);

  // Permanently erase every stored record (impacts AND low-g events —
  // there's no selective delete). `erasestore yes` is the armed form; the
  // yes token must NEVER be sent without this dialog (bare `erasestore` is
  // the safe report-only form). Completion is the erase_done sync event the
  // passive scanner watches for — the same event the BLE erase path emits.
  const handleEraseStore = useCallback(() => {
    const recs = devCfg?.records ?? 0;
    const kb =
      devCfg?.bytesUsed != null ? Math.round(devCfg.bytesUsed / 1024) : null;
    setConfirmReq({
      title: translate("wearableOps.erase.confirmTitle"),
      body:
        kb != null
          ? translate("wearableOps.erase.confirmBodyWithSize", {
              count: recs,
              kb,
            })
          : translate("wearableOps.erase.confirmBody", { count: recs }),
      confirmLabel: translate("wearableOps.erase.confirmLabel"),
      danger: true,
      action: async () => {
        setEraseBusy(true);
        await send("erasestore yes").catch(() => {});
        await new Promise((r) => setTimeout(r, 150));
        await kick?.().catch?.(() => {});
      },
    });
  }, [devCfg?.records, devCfg?.bytesUsed, send, kick]);

  useEffect(() => {
    if (!eraseDoneAt) return;
    setEraseDoneAt(null);
    setEraseBusy(false);
    showToast.success(translate("wearableOps.erase.done"));
    readConfig({ silent: true });
    // eslint-disable-next-line
  }, [eraseDoneAt]);

  // Watchdog: never leave the button spinning forever if erase_done was
  // missed (unplug mid-erase, etc.) — the next status re-poll tells the truth.
  useEffect(() => {
    if (!eraseBusy) return undefined;
    const timer = setTimeout(() => {
      setEraseBusy(false);
      showToast.error(translate("wearableOps.erase.noConfirmation"));
      readConfig({ silent: true });
    }, 90000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [eraseBusy]);

  // Start a session from the workbench (`session`, v0.5.81+ — same arc as a
  // physical button tap). Fire-and-forget with the queue-then-kick contract;
  // no status re-read needed: the pushed {"t":"state"} event flips the chip.
  const handleStartSession = useCallback(async () => {
    setCfgBusy("session");
    setStatusMsg(null);
    try {
      await send("session");
      await new Promise((r) => setTimeout(r, 150));
      await kick(); // nudge a deaf FIFO into draining the queued command
    } catch (err) {
      setStatusMsg(
        translate("wearableOps.status.startSessionFailed", {
          message: err.message,
        })
      );
    } finally {
      setCfgBusy(null);
    }
  }, [send, kick]);

  // ── Device configuration ──────────────────────────────────────────

  // Merge a `{"t":"status"}` reply (found among the given lines) into devCfg.
  const applyStatusLines = useCallback((lines) => {
    for (const l of lines || []) {
      const ev = parseFactoryLine(String(l));
      if (ev?.t === "status") {
        // Keep the device's own object, not just our derived view: a saved
        // export embeds it verbatim, and fields this app ignores today may
        // matter when the file is read back months from now.
        lastStatusRef.current = ev;
        const cfg = statusToDevCfg(ev);
        setDevCfg((prev) => ({ ...prev, ...cfg }));
        if (cfg.impactG != null) setImpactInput(String(cfg.impactG));
        if (cfg.eventG != null) setEventInput(String(cfg.eventG));
        return cfg;
      }
    }
    return null;
  }, []);
  applyStatusRef.current = applyStatusLines;

  // Read the current config by querying `status` (v0.5.38). Falls back to
  // scraping the menu banner on pre-0.5.38 firmware.
  const readConfig = useCallback(
    async ({ silent = false } = {}) => {
      setCfgBusy("read");
      try {
        const res = await executeCommand(STATUS_CMD);
        const cfg = applyStatusLines(res.lines);
        if (cfg) return cfg;
        // Pre-0.5.38 fallback: scrape the reprinted menu.
        const menu = await executeCommand(READ_CONFIG_CMD);
        const mcfg = parseMenuConfig(menu.lines);
        setDevCfg((prev) => ({ ...prev, ...mcfg }));
        if (mcfg.impactG != null) setImpactInput(String(mcfg.impactG));
        if (mcfg.eventG != null) setEventInput(String(mcfg.eventG));
        return mcfg;
      } catch (err) {
        if (!silent)
          setStatusMsg(
            translate("wearableOps.status.configReadFailed", {
              message: err.message,
            })
          );
        return null;
      } finally {
        setCfgBusy(null);
      }
    },
    [executeCommand, applyStatusLines]
  );

  // Read the device wall clock (v0.5.63) and merge utc/set into devCfg.
  const readTime = useCallback(async () => {
    try {
      const res = await executeCommand(TIME_CMD);
      for (const l of res.lines || []) {
        const ev = parseFactoryLine(String(l));
        if (ev?.t === "time") {
          setDevCfg((prev) => ({
            ...prev,
            timeUtc: ev.utc ?? null,
            timeSet: ev.set ?? prev?.timeSet,
          }));
          return ev;
        }
      }
    } catch {
      // clock read is best-effort; the status still carries time_set
    }
    return null;
  }, [executeCommand]);

  // Set the clock to host time, then re-read the clock + config so the badge and
  // calibration chip refresh.
  const syncTime = useCallback(async () => {
    setCfgBusy("time");
    try {
      await send(`time ${Date.now()}`);
      await new Promise((r) => setTimeout(r, 250));
      const clock = await readTime();
      await readConfig({ silent: true });
      return clock;
    } finally {
      setCfgBusy(null);
    }
  }, [send, readTime, readConfig]);

  // Read the clock once the console comes alive (the `ui on` handshake gives us
  // time_set, but not the actual UTC — that needs a `time` query).
  // If the clock isn't set, set it from host time right away (once per
  // link) and say so in a toast that stays up until dismissed — a wearable
  // with no clock stamps every record with uptime, which is easy to miss.
  const readTimeOnceRef = useRef(false);
  useEffect(() => {
    if (!consoleReady) {
      readTimeOnceRef.current = false;
      return;
    }
    if (readTimeOnceRef.current) return;
    readTimeOnceRef.current = true;
    (async () => {
      const clock = await readTime();
      const notSet = clock
        ? clock.set === false
        : devCfgRef.current?.timeSet === false;
      if (!notSet) return;
      const macRaw = devCfgRef.current?.mac || "";
      const mac =
        macRaw.replace(/(..)(?=.)/g, "$1:") ||
        translate("wearableOps.clockSync.unknownMac");
      const w = recordRef.current?.wearable;
      const label = w?.field_label_display ?? w?.field_label ?? null;
      const name = label != null ? `${label} (${mac})` : mac;
      try {
        const after = await syncTime();
        if (after && after.set === false) {
          showToast.error(translate("wearableOps.clockSync.failed", { name }));
        } else {
          showToast.sticky(translate("wearableOps.clockSync.synced", { name }));
        }
      } catch (err) {
        showToast.error(
          translate("wearableOps.clockSync.failedWithReason", {
            name,
            message: err.message,
          })
        );
      }
    })();
  }, [consoleReady, readTime, syncTime]);

  // Run one config/system command, then read `status` back so the UI shows the
  // device's applied (clamped) values — never what we merely sent. The command
  // is fired with a plain send() and the status read's kick-if-silent drains it,
  // so this works whether the setter acks (machine mode) or prints prose.
  const runCfgCmd = useCallback(
    async (key, spec, { refresh = true } = {}) => {
      setCfgBusy(key);
      setStatusMsg(null);
      try {
        await send(spec.command);
        if (refresh) {
          // Give the setter a beat to apply before the status read.
          await new Promise((r) => setTimeout(r, 300));
          const res = await executeCommand(STATUS_CMD);
          applyStatusLines(res.lines);
        } else {
          // Action commands (sync/dfu) don't report through status; nudge the
          // console so a deaf FIFO drains the queued command.
          await new Promise((r) => setTimeout(r, 150));
          await kick();
        }
        return true;
      } catch (err) {
        setStatusMsg(
          translate("wearableOps.status.commandFailed", {
            command: spec.command,
            message: err.message,
          })
        );
        return null;
      } finally {
        setCfgBusy(null);
      }
    },
    [send, executeCommand, kick, applyStatusLines]
  );

  // Opening the dialog IS the "what's missing" view: every requirement with

  const anyBusy = isExecuting || endingSession || passiveStreaming || !!cfgBusy;
  const flowActive =
    factoryMode || centrifugeMode || calibrateMode || updateMode;
  // Nothing to erase: bytes_used is the truth (events occupy the store too);
  // fall back to the counters on firmware without it.
  const storeEmpty =
    devCfg?.bytesUsed != null
      ? devCfg.bytesUsed === 0
      : (devCfg?.records ?? 0) + (devCfg?.events ?? 0) === 0;

  devCfg?.state === "CENTRIFUGE" && devCfg?.sub === "recording";

  // Modal-level live feed: survives tab switches and flow entry/exit, so the
  // Live view never rescans and replays the whole buffer on remount.
  const liveFeed = useLiveFeed({
    receivedData,
    connected,
    devRecords: devCfg?.records,
  });
  // Commands are only safe to send once the console has answered post-reboot.
  const commandsLocked = !consoleReady;

  // Forget the device the moment the connection drops — keyed off `connected`
  // (not the disconnect button) so a surprise unplug clears it too. Without
  // this the next connect briefly shows the previous device's state/battery.
  useEffect(() => {
    if (!connected) {
      setDevCfg(null);
      setImpactInput("");
      setEventInput("");
    }
  }, [connected]);

  // Manual retry. The machine is already retrying with backoff on its own --
  // this just skips the wait for an impatient operator, which is why it is an
  // affordance rather than the only route back. That distinction is the whole
  // difference from the old behaviour, where giving up was terminal.
  const handleRetryWake = link.retryNow;

  // Raw console: send exactly what's typed + the chosen line ending, so we can
  // discover which terminator the device's line reader accepts.
  const handleRawSend = useCallback(async () => {
    const text = consoleInput;
    if (!text) return;
    await sendRaw(text + consoleEnding);
  }, [consoleInput, consoleEnding, sendRaw]);

  // Collapse/expand the docked serial terminal (remembers its height).
  const toggleTerminal = useCallback(() => {
    setTerminalHeight((h) => {
      if (h > 0) {
        lastTerminalHeightRef.current = h;
        return 0;
      }
      return lastTerminalHeightRef.current || 160;
    });
  }, []);

  const handleClose = () => {
    // Escape / X while a capture is actually countdown/recording: closing
    // would unmount the flow with no idle and lose the run's UI. The flow's
    // own back button offers "keep armed & close" for the deliberate case.
    if (
      centrifugeMode &&
      devCfg?.state === "CENTRIFUGE" &&
      ["countdown", "recording"].includes(devCfg?.sub)
    ) {
      showToast.error(translate("wearableOps.toasts.captureRunning"));
      return;
    }
    if (bleDfuBusyRef.current) {
      showToast.error(translate("wearableOps.toasts.bleUpdateRunning"));
      return;
    }
    setBleDfuMode(false);
    // Flows don't survive an unmount; reset them so reopening starts fresh
    // instead of remounting a half-dead flow.
    setFactoryMode(false);
    setCentrifugeMode(false);
    setCalibrateMode(false);
    setStep(connected ? STEPS.CONNECTED : STEPS.SCAN);
    setExportResult(null);
    setAssignments(null);
    setStatusMsg(null);
    onClose();
  };

  const effectiveStep = connected ? STEPS.CONNECTED : step;
  const isFullConnected =
    fullScreen && effectiveStep === STEPS.CONNECTED && !bleDfuMode;

  const renderResults = () => {
    // The old code had NO branch here: a handshake that ran out of time left
    // this returning null, so the panel went blank with every control locked
    // and nothing on screen to act on. That was the dead end. The machine can
    // no longer reach a terminal state, and this renders the way out.
    if (link.phase === LINK.UNRESPONSIVE) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
          <Usb size={28} className="text-[#B45309]" />
          <div>
            <p className="text-sm font-medium text-[#374151]">
              {t("wearableOps.link.notAnswering")}
            </p>
            <p className="mx-auto mt-1 max-w-xs text-xs leading-relaxed text-[#9CA3AF]">
              {link.action?.detail}
            </p>
          </div>
          <button
            onClick={handleRetryWake}
            className={`rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-1.5 text-xs font-semibold text-[#B45309] transition duration-150 hover:bg-[#FEF3C7] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E07A3A]/45 focus-visible:ring-offset-1 focus-visible:ring-offset-white`}
          >
            {link.action?.label}
          </button>
          {/* Says the retry is alive without a spinner implying this attempt
              is the one that matters -- it is retrying either way. */}
          <p className="text-[11px] tabular-nums text-[#C4C7CD]">
            {t("wearableOps.link.attempts", { count: link.retryCount })}
          </p>
        </div>
      );
    }
    if (waking) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
          <RefreshCw size={28} className="animate-spin text-[#E07A3A]" />
          <div>
            <p className="text-sm font-medium text-[#6B7280]">
              {t("wearableOps.link.waking")}
            </p>
            <p className="mt-0.5 text-xs text-[#9CA3AF]">
              {t("wearableOps.link.wakingHint")}
            </p>
          </div>
        </div>
      );
    }
    if (exportBusy || endingSession || passiveStreaming) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3">
          <RefreshCw size={28} className="animate-spin text-[#E07A3A]" />
          <p className="text-sm text-[#6B7280]">
            {endingSession ? (
              t("wearableOps.export.endingSession")
            ) : exportBusy ? (
              exportStarted ? (
                <>
                  {t("wearableOps.export.streaming")}{" "}
                  <span className="font-semibold text-[#E07A3A]">
                    {exportTotal
                      ? t("wearableOps.export.progressOfTotal", {
                          count: exportProgress,
                          total: exportTotal,
                        })
                      : t("wearableOps.export.progress", {
                          count: exportProgress,
                        })}
                  </span>
                </>
              ) : (
                t("wearableOps.export.waitingForSession")
              )
            ) : (
              t("wearableOps.export.receiving")
            )}
          </p>
          {exportBusy && exportStarted && exportTotal > 0 && (
            <div className="w-64">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
                <div
                  className="h-full rounded-full bg-[#E07A3A] transition-all duration-300"
                  style={{
                    width: `${Math.min(
                      100,
                      Math.round((exportProgress / exportTotal) * 100)
                    )}%`,
                  }}
                />
              </div>
              <p className="mt-1 text-center font-mono text-[11px] tabular-nums text-[#9CA3AF]">
                {Math.min(
                  100,
                  Math.round((exportProgress / exportTotal) * 100)
                )}
                %
              </p>
            </div>
          )}
          {statusMsg && <p className="text-xs text-[#9CA3AF]">{statusMsg}</p>}
        </div>
      );
    }
    if (exportResult?.success) {
      return (
        <ExportResults
          parsed={exportResult.parsed}
          assignments={assignments}
          fieldLabel={
            record.wearable?.field_label_display ??
            record.wearable?.field_label ??
            null
          }
          onSaveFile={rawExportRef.current ? handleSaveExportFile : undefined}
          saveBusy={exportSaveBusy}
        />
      );
    }
    if (exportResult?.error) {
      return (
        <ErrorBanner
          message={exportResult.error}
          action={
            exportResult.refused ? (
              <button
                onClick={handleEndSession}
                className="flex-shrink-0 rounded-md bg-[#DC2626] px-2.5 py-1 text-[11px] font-medium text-white transition duration-150 hover:bg-[#B91C1C]"
              >
                {t("wearableOps.tools.endSession")}
              </button>
            ) : null
          }
        />
      );
    }
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F3F4F6]">
          <Activity size={24} className="text-[#D1D5DB]" />
        </div>
        <div>
          <p className="text-sm font-medium text-[#9CA3AF]">
            {t("wearableOps.export.emptyTitle")}
          </p>
          <p className="mt-0.5 text-xs text-[#D1D5DB]">
            {t("wearableOps.export.emptyHint")}
          </p>
        </div>
      </div>
    );
  };

  // The active flow (or export results) takes over the workbench center column.
  // null ⇒ the workbench shows its validation home + Overview/History sidebar.
  const centerFlow = factoryMode ? (
    <FactoryTestFlow
      send={send}
      sendRaw={sendRaw}
      kick={kick}
      receivedData={receivedData}
      connected={connected}
      commandsLocked={commandsLocked}
      onExit={() => setFactoryMode(false)}
    />
  ) : centrifugeMode ? (
    <CentrifugeFlow
      wearable={record.wearable}
      bleMac={devCfg?.mac}
      deviceState={devCfg?.state}
      deviceRecords={devCfg?.records}
      deviceFw={devCfg?.fw}
      exportRecords={exportRecords}
      exportReceived={exportProgress}
      exportTotal={exportTotal}
      send={send}
      kick={kick}
      receivedData={receivedData}
      connected={connected}
      commandsLocked={commandsLocked}
      onExit={() => {
        setCentrifugeMode(false);
        // Refresh state / record count after the mode (the poll was
        // suspended for the whole flow, so devCfg is stale by now).
        readConfig({ silent: true });
      }}
    />
  ) : calibrateMode ? (
    <CalibrationFlow
      deviceMac={devCfg?.mac}
      send={send}
      kick={kick}
      receivedData={receivedData}
      liveLineRef={liveLineRef}
      connected={connected}
      commandsLocked={commandsLocked}
      onExit={() => {
        setCalibrateMode(false);
        // Refresh cal_stored / clock after a run.
        readConfig({ silent: true });
      }}
    />
  ) : updateMode ? (
    <UpdateFirmwareFlow
      devCfg={devCfg}
      connected={connected}
      commandsLocked={commandsLocked}
      send={send}
      kick={kick}
      onExit={() => {
        setUpdateMode(false);
        readConfig({ silent: true });
      }}
    />
  ) : waking ||
    link.phase === LINK.UNRESPONSIVE ||
    exportBusy ||
    passiveStreaming ||
    endingSession ||
    exportResult ? (
    <>
      {exportResult && !exportBusy && !passiveStreaming && !endingSession && (
        <button
          onClick={() => setExportResult(null)}
          className="mb-3 flex items-center gap-1 text-xs font-medium text-[#6B7280] transition-colors duration-150 hover:text-[#E07A3A]"
        >
          <ChevronLeft size={14} />
          {t("wearableOps.export.backToDevice")}
        </button>
      )}
      {renderResults()}
    </>
  ) : null;

  // Device-tools popover content: diagnostic console, live settings, system mode
  // switches, the LED legend, plus End session / Sync clock / the terminal toggle.
  // Built here (not in the workbench) because it drives the modal's serial state.
  const deviceToolsContent = (
    <div className="space-y-2">
      <div className="flex gap-1.5">
        <button
          onClick={handleEndSession}
          disabled={anyBusy || commandsLocked}
          className="flex flex-1 items-center justify-center gap-2 rounded-[10px] border border-[#E5E7EB] bg-white px-3 py-2 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50"
        >
          {endingSession ? (
            <RefreshCw size={12} className="animate-spin" />
          ) : (
            <Power size={12} />
          )}
          {t("wearableOps.tools.endSession")}
          {devCfg?.state === "SESSION" && (
            <span className="rounded bg-[#FEF3E7] px-1.5 py-0.5 text-[10px] font-semibold text-[#E07A3A]">
              {t("wearableOps.tools.running")}
            </span>
          )}
        </button>
        <button
          onClick={() => syncTime()}
          disabled={anyBusy || commandsLocked}
          title={t("wearableOps.tools.syncClockTitle")}
          className="flex flex-1 items-center justify-center gap-2 rounded-[10px] border border-[#E5E7EB] bg-white px-3 py-2 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50"
        >
          <Clock size={12} className="text-[#E07A3A]" />
          {t("wearableOps.tools.syncClock")}
        </button>
      </div>

      <button
        onClick={toggleTerminal}
        className="flex w-full items-center justify-center gap-2 rounded-[10px] border border-[#E5E7EB] bg-white px-3 py-2 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
      >
        <Terminal size={12} className="text-[#6B7280]" />
        {terminalHeight > 0
          ? t("wearableOps.tools.hideTerminal")
          : t("wearableOps.tools.showTerminal")}
      </button>

      {/* Raw console (diagnostic) */}
      <CollapsibleSection
        icon={Terminal}
        title={t("wearableOps.tools.console")}
        summary={t("wearableOps.tools.consoleSummary")}
        open={openSections.console}
        onToggle={() => toggleSection("console")}
      >
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={consoleInput}
            onChange={(e) => setConsoleInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRawSend();
            }}
            placeholder={t("wearableOps.tools.commandPlaceholder")}
            className="min-w-0 flex-1 rounded-lg border border-[#E5E7EB] px-2.5 py-1.5 font-mono text-[12px] text-[#111827] outline-none focus:border-[#E07A3A]"
          />
          <select
            value={consoleEnding}
            onChange={(e) => setConsoleEnding(e.target.value)}
            title={t("wearableOps.tools.lineEndingTitle")}
            className="rounded-lg border border-[#E5E7EB] bg-white px-1.5 py-1.5 text-[11px] text-[#374151] outline-none"
          >
            <option value={"\n"}>LF</option>
            <option value={"\r"}>CR</option>
            <option value={"\r\n"}>CRLF</option>
            <option value={""}>{t("wearableOps.tools.lineEndingNone")}</option>
          </select>
          <button
            onClick={handleRawSend}
            disabled={!connected || !consoleInput}
            className="flex-shrink-0 rounded-lg bg-[#E07A3A] px-3 py-1.5 text-[12px] font-medium text-white transition duration-150 hover:bg-[#C9682E] disabled:opacity-40"
          >
            {t("wearableOps.tools.send")}
          </button>
          <button
            onClick={() => kick()}
            disabled={!connected}
            title={t("wearableOps.tools.kickTitle")}
            className="flex-shrink-0 rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-[12px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-40"
          >
            {t("wearableOps.tools.kick")}
          </button>
        </div>
      </CollapsibleSection>

      {/* Device settings — collapsed shows a live summary */}
      <CollapsibleSection
        icon={Settings2}
        title={t("wearableOps.settings.title")}
        summary={
          devCfg
            ? `${devCfg.impactG ?? "?"}g / ${devCfg.eventG ?? "?"}g · ${
                devCfg.profile || "?"
              }`
            : t("wearableOps.settings.notReadYet")
        }
        open={openSections.settings}
        onToggle={() => toggleSection("settings")}
        right={
          <button
            onClick={() => readConfig()}
            disabled={anyBusy || commandsLocked}
            title={t("wearableOps.settings.rereadTitle")}
            className="flex h-6 w-6 items-center justify-center rounded text-[#9CA3AF] transition duration-150 hover:bg-[#F3F4F6] hover:text-[#6B7280] disabled:opacity-40"
          >
            <RefreshCw
              size={12}
              className={cfgBusy === "read" ? "animate-spin" : ""}
            />
          </button>
        }
      >
        {!devCfg ? (
          <p className="rounded-lg border border-dashed border-[#E5E7EB] px-3 py-3 text-center text-[11px] text-[#9CA3AF]">
            {cfgBusy === "read"
              ? t("wearableOps.settings.reading")
              : t("wearableOps.settings.notReadHint")}
          </p>
        ) : (
          <div className="space-y-1.5">
            {/* Impact threshold */}
            <div className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2">
              <Zap size={13} className="flex-shrink-0 text-[#DC2626]" />
              <span className="flex-1 text-[12px] text-[#374151]">
                {t("wearableOps.settings.impactThreshold")}
              </span>
              <input
                type="number"
                min={IMPACT_G_MIN}
                max={IMPACT_G_MAX}
                value={impactInput}
                onChange={(e) => setImpactInput(e.target.value)}
                disabled={anyBusy || commandsLocked}
                className="w-14 rounded border border-[#E5E7EB] px-1.5 py-0.5 text-right text-[12px] text-[#111827] disabled:opacity-50"
              />
              <span className="text-[11px] text-[#9CA3AF]">g</span>
              <button
                onClick={() =>
                  runCfgCmd("impact", CFG_CMDS.impact(Number(impactInput)))
                }
                disabled={
                  anyBusy ||
                  commandsLocked ||
                  impactInput === "" ||
                  Number(impactInput) === devCfg.impactG
                }
                className="rounded bg-[#E07A3A] px-2 py-0.5 text-[11px] font-semibold text-white transition duration-150 hover:bg-[#C9682E] disabled:opacity-40"
              >
                {cfgBusy === "impact" ? "…" : t("wearableOps.settings.set")}
              </button>
            </div>

            {/* Event threshold */}
            <div className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2">
              <Waves size={13} className="flex-shrink-0 text-[#0EA5E9]" />
              <span className="flex-1 text-[12px] text-[#374151]">
                {t("wearableOps.settings.eventThreshold")}
                <span className="ml-1 text-[10px] text-[#9CA3AF]">
                  {t("wearableOps.settings.eventThresholdHint")}
                </span>
              </span>
              <input
                type="number"
                min={0}
                max={EVENT_G_MAX}
                value={eventInput}
                onChange={(e) => setEventInput(e.target.value)}
                disabled={anyBusy || commandsLocked}
                className="w-14 rounded border border-[#E5E7EB] px-1.5 py-0.5 text-right text-[12px] text-[#111827] disabled:opacity-50"
              />
              <span className="text-[11px] text-[#9CA3AF]">g</span>
              <button
                onClick={() =>
                  runCfgCmd("event", CFG_CMDS.event(Number(eventInput)))
                }
                disabled={
                  anyBusy ||
                  commandsLocked ||
                  eventInput === "" ||
                  Number(eventInput) === devCfg.eventG
                }
                className="rounded bg-[#E07A3A] px-2 py-0.5 text-[11px] font-semibold text-white transition duration-150 hover:bg-[#C9682E] disabled:opacity-40"
              >
                {cfgBusy === "event" ? "…" : t("wearableOps.settings.set")}
              </button>
            </div>

            {/* IMU profile */}
            <div className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2">
              <Cpu size={13} className="flex-shrink-0 text-[#6B7280]" />
              <span className="flex-1 text-[12px] text-[#374151]">
                {t("wearableOps.recordDetail.specs.imuProfile")}
              </span>
              <span className="text-[12px] font-semibold text-[#111827]">
                {devCfg.profile || "—"}
              </span>
              <button
                onClick={() => runCfgCmd("profile", CFG_CMDS.profile())}
                disabled={anyBusy || commandsLocked}
                className="rounded border border-[#E5E7EB] px-2 py-0.5 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-40"
              >
                {cfgBusy === "profile" ? "…" : t("wearableOps.settings.cycle")}
              </button>
            </div>

            {/* Broadcast mode */}
            <div className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2">
              <Activity size={13} className="flex-shrink-0 text-[#6B7280]" />
              <span className="flex-1 text-[12px] text-[#374151]">
                {t("wearableOps.settings.broadcast")}
              </span>
              <span className="text-[12px] font-semibold text-[#111827]">
                {devCfg.broadcast || "—"}
              </span>
              <button
                onClick={() => runCfgCmd("broadcast", CFG_CMDS.broadcast())}
                disabled={anyBusy || commandsLocked}
                className="rounded border border-[#E5E7EB] px-2 py-0.5 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-40"
              >
                {cfgBusy === "broadcast"
                  ? "…"
                  : t("wearableOps.settings.toggle")}
              </button>
            </div>

            {/* Live broadcast (cast_tx, fw v0.5.83+ — feature-detected) */}
            {devCfg.castTx !== undefined && (
              <div
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${
                  devCfg.castTx
                    ? "border-[#E5E7EB] bg-white"
                    : "border-[#FDE68A] bg-[#FFFBEB]"
                }`}
              >
                <RadioTower
                  size={13}
                  className={`flex-shrink-0 ${
                    devCfg.castTx ? "text-[#6B7280]" : "text-[#B45309]"
                  }`}
                />
                <span className="flex-1 text-[12px] text-[#374151]">
                  {t("wearableOps.settings.liveBroadcast")}
                </span>
                <span
                  className={`text-[12px] font-semibold ${
                    devCfg.castTx ? "text-[#111827]" : "text-[#B45309]"
                  }`}
                  title={
                    devCfg.castTx
                      ? t("wearableOps.settings.castOnTitle")
                      : t("wearableOps.settings.castOffTitle")
                  }
                >
                  {devCfg.castTx
                    ? t("wearableOps.settings.castOn")
                    : t("wearableOps.settings.castOff")}
                </span>
                <button
                  onClick={() => runCfgCmd("castTx", CFG_CMDS.castTx())}
                  disabled={anyBusy || commandsLocked}
                  className="rounded border border-[#E5E7EB] bg-white px-2 py-0.5 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-40"
                >
                  {cfgBusy === "castTx"
                    ? "…"
                    : t("wearableOps.settings.toggle")}
                </button>
              </div>
            )}

            {/* Radio PHY */}
            <div className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2">
              <Radio size={13} className="flex-shrink-0 text-[#6B7280]" />
              <span className="flex-1 text-[12px] text-[#374151]">
                {t("wearableOps.settings.radioPhy")}
              </span>
              <span className="text-[12px] font-semibold text-[#111827]">
                {devCfg.phy || "—"}
              </span>
              <button
                onClick={() => runCfgCmd("phy", CFG_CMDS.phy())}
                disabled={anyBusy || commandsLocked}
                className="rounded border border-[#E5E7EB] px-2 py-0.5 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-40"
              >
                {cfgBusy === "phy" ? "…" : t("wearableOps.settings.toggle")}
              </button>
            </div>

            {/* Device info + battery */}
            <div className="flex items-center gap-2 rounded-lg bg-[#F9FAFB] px-3 py-2">
              <span className="flex-1 truncate text-[11px] text-[#6B7280]">
                {devCfg.fw || t("wearableOps.settings.fwUnknown")} ·{" "}
                {devCfg.state || t("wearableOps.settings.stateUnknown")}
                {devCfg.battPct != null || devCfg.charge
                  ? ` · ${batteryDisplay(devCfg).label}${
                      devCfg.battMv ? ` (${devCfg.battMv} mV)` : ""
                    }`
                  : ""}
              </span>
              <button
                onClick={() => runCfgCmd("battery", CFG_CMDS.battery())}
                disabled={anyBusy || commandsLocked}
                className="rounded border border-[#E5E7EB] bg-white px-2 py-0.5 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F3F4F6] disabled:opacity-40"
              >
                {cfgBusy === "battery" ? "…" : t("common.actions.refresh")}
              </button>
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* System (mode switches) */}
      <CollapsibleSection
        icon={Power}
        title={t("wearableOps.system.title")}
        summary={t("wearableOps.system.summary")}
        open={openSections.system}
        onToggle={() => toggleSection("system")}
      >
        <div className="flex gap-1.5">
          <button
            onClick={() =>
              runCfgCmd("sync", CFG_CMDS.sync(), { refresh: false })
            }
            disabled={anyBusy || commandsLocked}
            title={t("wearableOps.system.syncModeTitle")}
            className="flex-1 rounded-lg border border-[#E5E7EB] bg-white px-2 py-1.5 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-40"
          >
            {cfgBusy === "sync" ? "…" : t("wearableOps.system.syncMode")}
          </button>
          <button
            onClick={() => setUpdateMode(true)}
            disabled={anyBusy || commandsLocked}
            title={t("wearableOps.system.updateFirmwareTitle")}
            className="flex-1 rounded-lg border border-[#E5E7EB] bg-white px-2 py-1.5 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-40"
          >
            {t("wearableOps.system.updateFirmware")}
          </button>
          <button
            onClick={() =>
              setConfirmReq({
                title: t("wearableOps.system.powerOffConfirmTitle"),
                body: t("wearableOps.system.powerOffConfirmBody"),
                confirmLabel: t("wearableOps.system.powerOff"),
                danger: true,
                action: () => send("off").catch(() => {}),
              })
            }
            disabled={anyBusy || commandsLocked}
            title={t("wearableOps.system.powerOffTitle")}
            className="flex-1 rounded-lg border border-[#FEE2E2] bg-white px-2 py-1.5 text-[11px] font-medium text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2] disabled:opacity-40"
          >
            {t("wearableOps.system.powerOff")}
          </button>
        </div>
        {fwAtLeast(devCfg?.fw, ERASE_FW_MIN) && (
          <button
            onClick={handleEraseStore}
            disabled={
              anyBusy ||
              commandsLocked ||
              eraseBusy ||
              storeEmpty ||
              devCfg?.state === "SESSION" ||
              (devCfg?.state === "CENTRIFUGE" && devCfg?.sub === "recording")
            }
            title={
              storeEmpty
                ? t("wearableOps.erase.storeEmpty")
                : devCfg?.state === "SESSION"
                ? t("wearableOps.erase.endSessionFirst")
                : devCfg?.state === "CENTRIFUGE" && devCfg?.sub === "recording"
                ? t("wearableOps.erase.stopRecordingFirst")
                : t("wearableOps.erase.buttonTitle")
            }
            className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#FEE2E2] bg-white px-2 py-1.5 text-[11px] font-medium text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2] disabled:opacity-40"
          >
            {eraseBusy ? (
              <>
                <RefreshCw size={11} className="animate-spin" />
                {liveFeed?.eraseProgress?.pct != null
                  ? t("wearableOps.erase.erasingPct", {
                      pct: liveFeed.eraseProgress.pct,
                    })
                  : t("wearableOps.erase.erasing")}
              </>
            ) : (
              <>
                <Trash2 size={11} />
                {t("wearableOps.erase.button")}
              </>
            )}
          </button>
        )}
      </CollapsibleSection>

      {/* LED legend */}
      <CollapsibleSection
        icon={Lightbulb}
        title={t("wearableOps.leds.title")}
        summary={
          ledLegend.length
            ? t("wearableOps.leds.states", { count: ledLegend.length })
            : t("wearableOps.leds.legend")
        }
        open={openSections.leds}
        onToggle={() => toggleSection("leds")}
        right={
          <button
            onClick={() => send("leds").catch(() => {})}
            disabled={commandsLocked}
            title={t("wearableOps.leds.requeryTitle")}
            className="rounded border border-[#E5E7EB] bg-white px-1.5 py-0.5 text-[10px] font-medium text-[#6B7280] transition duration-150 hover:bg-[#F3F4F6] disabled:opacity-40"
          >
            {t("common.actions.refresh")}
          </button>
        }
      >
        {ledLegend.length === 0 ? (
          <p className="text-[11px] text-[#9CA3AF]">
            {t("wearableOps.leds.empty")}
          </p>
        ) : (
          <div className="space-y-0.5">
            {ledLegend.map((led) => {
              const active =
                led.state === ledStateSlug(devCfg?.state, devCfg?.sub);
              const hex =
                LED_COLOR_HEX[String(led.color || "").toUpperCase()] ||
                "#9CA3AF";
              return (
                <div
                  key={led.state}
                  className={`flex items-start gap-2 rounded-md px-1.5 py-1 ${
                    active ? "bg-[#FFF3ED]" : ""
                  }`}
                >
                  <span
                    className="ring-black/10 mt-0.5 h-3 w-3 flex-shrink-0 rounded-full ring-1"
                    style={{ backgroundColor: hex }}
                  />
                  <div className="min-w-0">
                    <p
                      className={`text-[11px] font-medium ${
                        active ? "text-[#E07A3A]" : "text-[#374151]"
                      }`}
                    >
                      {String(led.state || "")
                        .replace(/-/g, " ")
                        .replace(/^\w/, (c) => c.toUpperCase())}
                      {active && (
                        <span className="ml-1 text-[9px] font-semibold uppercase text-[#E07A3A]">
                          {t("wearableOps.leds.now")}
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] text-[#9CA3AF]">{led.pattern}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleSection>
    </div>
  );

  return (
    <Modal
      show={show}
      onClose={handleClose}
      maxWidth="520px"
      fullScreen={fullScreen}
    >
      {/* Header — the connected workbench renders its own top bar */}
      {!isFullConnected && (
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{
            background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
          }}
        >
          <h2 className="text-base font-semibold text-white">
            {t("wearableOps.modal.title")}
          </h2>
          <button
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition duration-150 hover:bg-white/10 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>
      )}

      <div
        className={`min-h-0 flex-1 overflow-hidden ${
          isFullConnected ? "flex flex-col" : "overflow-y-auto"
        }`}
      >
        {(compareMode || compareSeed) && (
          <CompareExportsView
            seed={compareSeed}
            onExit={() => {
              setCompareMode(false);
              setCompareSeed(null);
            }}
          />
        )}
        {bleDfuMode && !compareMode && !compareSeed && (
          <div className="px-6 py-5">
            <BluetoothDfuFlow
              onExit={() => setBleDfuMode(false)}
              onBusyChange={setBleDfuBusy}
              showVersions={isSuperUser}
            />
          </div>
        )}
        {!bleDfuMode && !compareMode && !compareSeed && importedExport && (
          <div className="overflow-y-auto px-6 py-5">
            <button
              onClick={() => setImportedExport(null)}
              className="mb-3 flex items-center gap-1 text-xs font-medium text-[#6B7280] transition-colors duration-150 hover:text-[#E07A3A]"
            >
              <ChevronLeft size={14} />
              {t("common.actions.back")}
            </button>
            <ExportResults
              parsed={importedExport.parsed}
              assignments={null}
              fieldLabel={
                importedExport.wearable?.field_label_display ??
                importedExport.wearable?.field_label ??
                null
              }
              imported={{
                wearableName: wearableShortName(
                  importedExport.wearable,
                  importedExport.wearable?.ble_mac
                ),
                exportedAt: importedExport.exportedAt,
                fileName: importedExport.fileName,
              }}
              fileReview={importedExport.review}
              onSaveFile={handleSaveImportedFile}
              saveBusy={exportSaveBusy}
              saveLabel={t("wearableOps.modal.saveTagging")}
              saveTitle={t("wearableOps.modal.saveTaggingTitle")}
              onCompare={() =>
                setCompareSeed({
                  name: importedExport.fileName,
                  text: importedExport.contents,
                })
              }
            />
          </div>
        )}
        {!bleDfuMode &&
          !compareMode &&
          !compareSeed &&
          !importedExport &&
          effectiveStep === STEPS.SCAN && (
            <div className="px-6 py-5">
              {/* Fleet review works without a device: saved exports open here. */}
              <div className="mb-1 flex justify-end">
                <button
                  onClick={handleOpenExportFile}
                  className="flex items-center gap-1.5 text-[12px] font-medium text-[#6B7280] transition duration-150 hover:text-[#E07A3A]"
                >
                  <FolderOpen size={13} />
                  {t("wearableOps.scan.openSavedExport")}
                </button>
                <button
                  onClick={() => setCompareMode(true)}
                  className="ml-4 flex items-center gap-1.5 text-[12px] font-medium text-[#6B7280] transition duration-150 hover:text-[#E07A3A]"
                >
                  <GitCompareArrows size={13} />
                  {t("wearableOps.scan.compareExports")}
                </button>
                {/* Units whose firmware can't talk over USB (e.g. factory
                    images) but are advertising for an update. */}
                {isBleDfuSupported() && (
                  <button
                    onClick={() => setBleDfuMode(true)}
                    className="ml-4 flex items-center gap-1.5 text-[12px] font-medium text-[#6B7280] transition duration-150 hover:text-[#E07A3A]"
                  >
                    <Bluetooth size={13} />
                    {t("wearableOps.scan.updateOverBluetooth")}
                  </button>
                )}
              </div>
              {/* Illustration with scanning pulse */}
              <div className="mb-5 flex flex-col items-center text-center">
                <div className="relative mb-4 flex items-center justify-center">
                  <AnimatePresence>
                    {scanning && (
                      <>
                        {[0, 1, 2].map((i) => (
                          <motion.div
                            key={`pulse-${i}`}
                            className="absolute rounded-full border border-[#E07A3A]"
                            initial={{ width: 64, height: 64, opacity: 0.5 }}
                            animate={{
                              width: [64, 140],
                              height: [64, 140],
                              opacity: [0.4, 0],
                            }}
                            transition={{
                              duration: 1.5,
                              repeat: Infinity,
                              delay: i * 0.5,
                              ease: "easeOut",
                            }}
                          />
                        ))}
                      </>
                    )}
                  </AnimatePresence>
                  {!scanning && (
                    <div
                      className="absolute h-24 w-24 rounded-full"
                      style={{
                        background:
                          "radial-gradient(circle, rgba(232, 133, 74, 0.12) 0%, rgba(232, 133, 74, 0) 70%)",
                      }}
                    />
                  )}
                  <motion.div
                    className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FEF3E7]"
                    animate={scanning ? { scale: [1, 1.05, 1] } : { scale: 1 }}
                    transition={
                      scanning
                        ? { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
                        : { duration: 0.2 }
                    }
                  >
                    <Usb size={32} className="text-[#E07A3A]" />
                  </motion.div>
                </div>
                <h3 className="mb-1 text-[15px] font-semibold text-[#111827]">
                  {scanning || connecting
                    ? t("wearableOps.scan.looking")
                    : t("wearableOps.scan.plugIn")}
                </h3>
                <p className="max-w-[340px] text-sm text-[#6B7280]">
                  {scanning || connecting
                    ? t("wearableOps.scan.detecting")
                    : t("wearableOps.scan.plugInHint")}
                </p>
              </div>

              {error && (
                <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#FEF2F2] px-3 py-2.5">
                  <AlertCircle
                    size={14}
                    className="flex-shrink-0 text-[#DC2626]"
                  />
                  <p className="text-xs text-[#DC2626]">{error}</p>
                </div>
              )}

              {/* Port list */}
              <AnimatePresence mode="wait">
                {ports.length > 0 && !scanning && (
                  <motion.div
                    key={`ports-${scanKeyRef.current}`}
                    className="mb-4 space-y-2"
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={{
                      visible: { transition: { staggerChildren: 0.08 } },
                      hidden: {},
                    }}
                  >
                    {[...ports]
                      .sort(
                        (a, b) =>
                          (isWearablePort(b) ? 1 : 0) -
                          (isWearablePort(a) ? 1 : 0)
                      )
                      .map((port) => {
                        const isSelected = selectedPort === port.path;
                        const isWearable = isWearablePort(port);
                        return (
                          <motion.button
                            key={port.path}
                            onClick={() => setSelectedPort(port.path)}
                            variants={{
                              hidden: { opacity: 0, y: 12, scale: 0.97 },
                              visible: { opacity: 1, y: 0, scale: 1 },
                            }}
                            transition={{ duration: 0.25, ease: "easeOut" }}
                            className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition duration-150 ${
                              isSelected
                                ? "border-[#E07A3A] bg-[#FEF7F3]"
                                : isWearable
                                ? "border-[#F3D9C6] bg-white hover:border-[#E07A3A]"
                                : "border-[#E5E7EB] bg-white hover:border-[#D1D5DB] hover:bg-[#F9FAFB]"
                            }`}
                          >
                            <div
                              className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                                isSelected
                                  ? "bg-[#E07A3A]"
                                  : isWearable
                                  ? "bg-[#FEF3E7]"
                                  : "bg-[#F3F4F6]"
                              }`}
                            >
                              {isSelected ? (
                                <Check size={14} className="text-white" />
                              ) : (
                                <Usb
                                  size={14}
                                  className={
                                    isWearable
                                      ? "text-[#E07A3A]"
                                      : "text-[#6B7280]"
                                  }
                                />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="flex items-center gap-1.5 truncate text-[13px] font-medium text-[#111827]">
                                {port.manufacturer || port.path}
                                {isWearable && (
                                  <span className="flex-shrink-0 rounded bg-[#FEF3E7] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#E07A3A]">
                                    {t("common.entities.wearable_one")}
                                  </span>
                                )}
                              </p>
                              <p className="truncate text-[11px] tabular-nums text-[#9CA3AF]">
                                {port.path}
                                {port.serialNumber
                                  ? t("wearableOps.scan.serialNumber", {
                                      sn: port.serialNumber,
                                    })
                                  : ""}
                              </p>
                            </div>
                          </motion.button>
                        );
                      })}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleScan}
                  disabled={scanning}
                  className={`flex items-center justify-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-semibold transition duration-150 disabled:opacity-60 ${
                    ports.length > 0
                      ? "border border-[#E5E7EB] bg-white font-medium text-[#374151] hover:bg-[#F9FAFB]"
                      : "w-full text-white"
                  }`}
                  style={
                    ports.length === 0
                      ? {
                          background:
                            "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                        }
                      : undefined
                  }
                >
                  {scanning ? (
                    <RefreshCw size={14} className="animate-spin" />
                  ) : (
                    <Search size={14} />
                  )}
                  {scanning
                    ? t("wearableOps.scan.scanning")
                    : ports.length > 0
                    ? t("wearableOps.scan.rescan")
                    : t("wearableOps.scan.scanForDevices")}
                </button>
                {ports.length > 0 && (
                  <button
                    onClick={handleConnect}
                    disabled={!selectedPort || connecting}
                    className="flex flex-1 items-center justify-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-40"
                    style={{
                      background:
                        "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                    }}
                  >
                    {connecting ? (
                      <>
                        <RefreshCw size={14} className="animate-spin" />
                        {t("wearableOps.scan.connecting")}
                      </>
                    ) : (
                      t("wearableOps.scan.connect")
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

        {!bleDfuMode &&
          !compareMode &&
          !compareSeed &&
          !importedExport &&
          effectiveStep === STEPS.CONNECTED && (
            <>
              <DeviceWorkbench
                lastStatusAt={aliveAt}
                wearable={record.wearable}
                recordStatus={record.recordStatus}
                errorMsg={record.errorMsg}
                refreshing={record.refreshing}
                historyKey={record.historyKey}
                handleUpdated={record.handleUpdated}
                onRefresh={() => {
                  // A locked console can't answer a config read — retry the wake
                  // handshake instead so refresh doubles as recovery.
                  if (commandsLocked) {
                    handleRetryWake();
                    return;
                  }
                  record.refresh();
                  readConfig({ silent: true });
                }}
                devCfg={devCfg}
                connected={connected}
                currentPort={currentPort}
                waking={waking}
                commandsLocked={commandsLocked}
                anyBusy={anyBusy}
                liveFeed={liveFeed}
                flowActive={flowActive}
                deviceRegistered={deviceRegistered}
                onClose={handleClose}
                onDisconnect={handleDisconnect}
                onExport={() => handleExport()}
                exportBusy={exportBusy}
                linesReceived={exportProgress}
                exportTotal={exportTotal}
                onFactoryTest={() => setFactoryMode(true)}
                onCentrifuge={() => setCentrifugeMode(true)}
                onCalibrate={() => setCalibrateMode(true)}
                onStartSession={handleStartSession}
                onGoIdle={handleEndSession}
                sessionBusy={cfgBusy === "session" || endingSession}
                onSyncTime={syncTime}
                onUpdateFirmware={() => setUpdateMode(true)}
                onEraseAll={handleEraseStore}
                storeEmpty={storeEmpty}
                centerFlow={centerFlow}
                // Export results own the whole width: the review workspace is
                // a video beside every impact on the device, not a form.
                centerFlowWide={
                  !!exportResult &&
                  !factoryMode &&
                  !calibrateMode &&
                  !updateMode
                }
                deviceTools={deviceToolsContent}
              />

              {/* Docked resizable serial terminal (collapsed by default; the
                Device tools "Show serial terminal" toggle opens it) */}
              <div className="flex-shrink-0 bg-[#12141C]">
                {terminalHeight > 0 && (
                  <div
                    onMouseDown={handleTerminalResizeStart}
                    className="group flex h-2 cursor-row-resize items-center justify-center bg-[#0C0E14] transition-colors hover:bg-[#E07A3A]/25"
                    title={t("wearableOps.modal.resizeTerminal")}
                  >
                    <span className="h-0.5 w-8 rounded-full bg-[#3A4150] transition-colors group-hover:bg-[#E07A3A]" />
                  </div>
                )}
                <SerialTerminal
                  receivedData={receivedData}
                  connected={connected}
                  docked
                  terminalHeight={terminalHeight}
                  collapsed={terminalHeight === 0}
                  onToggleCollapse={toggleTerminal}
                />
              </div>
            </>
          )}
      </div>
      <ConfirmDialog
        open={!!confirmReq}
        title={confirmReq?.title}
        body={confirmReq?.body}
        details={confirmReq?.details}
        confirmLabel={confirmReq?.confirmLabel}
        danger={confirmReq?.danger}
        onCancel={() => setConfirmReq(null)}
        onConfirm={() => {
          const action = confirmReq?.action;
          setConfirmReq(null);
          action?.();
        }}
      />
    </Modal>
  );
};

export default WearableOpsModal;
